document.documentElement.style.setProperty(
  "--window-height",
  window.innerHeight + "px"
);

// ── SVG Export helpers ────────────────────────────────────────────────────────

class FontLoader {
  constructor() { this._cache = new Map(); this._loading = new Map(); }

  load(fontName, fontUrl) {
    if (this._cache.has(fontName)) return Promise.resolve(this._cache.get(fontName));
    if (this._loading.has(fontName)) return this._loading.get(fontName);
    const promise = fetch(fontUrl)
      .then(r => r.arrayBuffer())
      .then(async buf => {
        const sig = String.fromCharCode(...new Uint8Array(buf, 0, 4));
        let parseBuf = buf;
        if (sig === 'wOF2') {
          ////console.log('[FontLoader] WOFF2 detected for', fontName, '— decompressing via wawoff2');
          const woff2Module = await (typeof woff2Init !== 'undefined' ? woff2Init : Promise.reject(new Error('woff2Init not available')));
          const sfnt = woff2Module.decompress(new Uint8Array(buf));
          parseBuf = sfnt.buffer.slice(sfnt.byteOffset, sfnt.byteOffset + sfnt.byteLength);
        }
        const f = opentype.parse(parseBuf);
        this._cache.set(fontName, f);
        this._loading.delete(fontName);
        return f;
      })
      .catch(err => { this._loading.delete(fontName); console.warn('[FontLoader]', fontName, err); return null; });
    this._loading.set(fontName, promise);
    return promise;
  }

  get(fontName) { return this._cache.get(fontName) || null; }
}
const fontLoader = new FontLoader();

function buildClipPathD(rx, ry, w, h) {
  if (rx >= w / 2 && ry >= h / 2) {
    const cx = w / 2, cy = h / 2;
    return `M ${cx} 0 A ${cx} ${cy} 0 1 1 ${cx - 0.001} 0 Z`;
  } else if (rx > 0 || ry > 0) {
    return `M ${rx} 0 L ${w - rx} 0 Q ${w} 0 ${w} ${ry} L ${w} ${h - ry} Q ${w} ${h} ${w - rx} ${h} L ${rx} ${h} Q 0 ${h} 0 ${h - ry} L 0 ${ry} Q 0 0 ${rx} 0 Z`;
  } else {
    return `M 0 0 H ${w} V ${h} H 0 Z`;
  }
}

function generateTextSVG(font, text, rectWidth, rectHeight, fontSizePx, rx, ry) {
  function wrapParagraph(paragraph, sizePx, maxWidth) {
    const words = paragraph.split(' ');
    const lines = [];
    let line = '';
    for (const w of words) {
      const test = line ? line + ' ' + w : w;
      if (font.getAdvanceWidth(test, sizePx) <= maxWidth) { line = test; }
      else { if (line) lines.push(line); line = w; }
    }
    if (line) lines.push(line);
    return lines;
  }

  const paragraphs = text.split(/\r?\n/);
  let sizePx = fontSizePx;
  let lineHeight = sizePx * 1.2;

  let lines = [];
  for (const p of paragraphs) lines.push(...wrapParagraph(p, sizePx, rectWidth));

  let maxW = Math.max(...lines.map(l => font.getAdvanceWidth(l, sizePx)));
  while ((maxW > rectWidth || lines.length * lineHeight > rectHeight) && sizePx > 5) {
    sizePx *= 0.95;
    lineHeight = sizePx * 1.2;
    lines = [];
    for (const p of paragraphs) lines.push(...wrapParagraph(p, sizePx, sizePx));
    maxW = Math.max(...lines.map(l => font.getAdvanceWidth(l, sizePx)));
  }

  const midlineOffset = ((font.ascender + font.descender) / font.unitsPerEm) * sizePx / 2;
  const totalH = lines.length * lineHeight;
  const startY = -totalH / 2 + lineHeight / 2;

  const pathEls = lines.map((lineText, i) => {
    const advW = font.getAdvanceWidth(lineText, sizePx);
    const x = rectWidth / 2 - advW / 2;
    const y = rectHeight / 2 + startY + i * lineHeight + midlineOffset;
    const d = font.getPath(lineText, x, y, sizePx).toPathData(2);
    return `<path d="${d}" fill="rgba(0,0,0,0.3)"/>`;
  }).join('');

  const clipD = buildClipPathD(rx, ry, rectWidth, rectHeight);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${rectWidth} ${rectHeight}">
  <defs><clipPath id="c"><path d="${clipD}"/></clipPath></defs>
  <g clip-path="url(#c)">${pathEls}</g>
</svg>`;
}

function generateCurvedTextSVG(font, text, rectWidth, rectHeight, fontSizePx, rx, ry, curvedTextStart) {
  const w = rectWidth, h = rectHeight;
  const straightTop    = w - 2 * rx;
  const straightRight  = h - 2 * ry;
  const straightBottom = w - 2 * rx;
  const straightLeft   = h - 2 * ry;
  const a = rx, b = ry;
  const fullEllipseCirc = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
  const cornerLength = fullEllipseCirc / 4;
  const P = straightTop + cornerLength + straightRight + cornerLength
          + straightBottom + cornerLength + straightLeft + cornerLength;

  const startDeg = (curvedTextStart || 0) % 360;
  const startOffset = (startDeg / 360) * P;

  const chars = Array.from(text);
  const charWidths = chars.map(ch => font.getAdvanceWidth(ch, fontSizePx));

  const isBottomHalf = startDeg > 90 && startDeg < 270;
  if (isBottomHalf) { chars.reverse(); charWidths.reverse(); }

  const letterSpacing = fontSizePx * 0.3;
  const cumulative = [0];
  for (let i = 0; i < charWidths.length; i++) {
    cumulative.push(cumulative[i] + charWidths[i] + letterSpacing);
  }
  const totalTextWidth = cumulative[cumulative.length - 1];
  const centeredStartOffset = startOffset - totalTextWidth / 2;

  const ascenderOffset  = (font.ascender  / font.unitsPerEm) * fontSizePx;
  const descenderOffset = (font.descender / font.unitsPerEm) * fontSizePx; // negative

  const pathEls = [];
  for (let i = 0; i < chars.length; i++) {
    const halfChar = charWidths[i] / 2;
    const distanceFromStart = cumulative[i] + halfChar;
    if (distanceFromStart >= P) break;

    const rawOffset = centeredStartOffset + distanceFromStart;
    const offsetAlong = ((rawOffset % P) + P) % P;

    let localX = 0, localY = 0, tangentAngle = 0;

    if (offsetAlong < straightTop) {
      localX = -w / 2 + rx + offsetAlong;
      localY = -h / 2;
      tangentAngle = 0;
    } else if (offsetAlong < straightTop + cornerLength) {
      const t = offsetAlong - straightTop;
      const φ = -Math.PI / 2 + (t / cornerLength) * (Math.PI / 2);
      const cx = w / 2 - rx;
      const cy = -h / 2 + ry;
      localX = cx + rx * Math.cos(φ);
      localY = cy + ry * Math.sin(φ);
      tangentAngle = φ + Math.PI / 2;
    } else if (offsetAlong < straightTop + cornerLength + straightRight) {
      const t = offsetAlong - (straightTop + cornerLength);
      localX = w / 2;
      localY = -h / 2 + ry + t;
      tangentAngle = Math.PI / 2;
    } else if (offsetAlong < straightTop + cornerLength + straightRight + cornerLength) {
      const t = offsetAlong - (straightTop + cornerLength + straightRight);
      const φ = (t / cornerLength) * (Math.PI / 2);
      const cx = w / 2 - rx;
      const cy = h / 2 - ry;
      localX = cx + rx * Math.cos(φ);
      localY = cy + ry * Math.sin(φ);
      tangentAngle = φ + Math.PI / 2;
    } else if (offsetAlong < straightTop + cornerLength + straightRight + cornerLength + straightBottom) {
      const t = offsetAlong - (straightTop + cornerLength + straightRight + cornerLength);
      localX = w / 2 - rx - t;
      localY = h / 2;
      tangentAngle = Math.PI;
    } else if (offsetAlong < straightTop + cornerLength + straightRight + cornerLength + straightBottom + cornerLength) {
      const t = offsetAlong - (straightTop + cornerLength + straightRight + cornerLength + straightBottom);
      const φ = Math.PI / 2 + (t / cornerLength) * (Math.PI / 2);
      const cx = -w / 2 + rx;
      const cy = h / 2 - ry;
      localX = cx + rx * Math.cos(φ);
      localY = cy + ry * Math.sin(φ);
      tangentAngle = φ + Math.PI / 2;
    } else if (offsetAlong < straightTop + cornerLength + straightRight + cornerLength + straightBottom + cornerLength + straightLeft) {
      const t = offsetAlong - (straightTop + cornerLength + straightRight + cornerLength + straightBottom + cornerLength);
      localX = -w / 2;
      localY = h / 2 - ry - t;
      tangentAngle = (3 * Math.PI) / 2;
    } else {
      const t = offsetAlong - (straightTop + cornerLength + straightRight + cornerLength + straightBottom + cornerLength + straightLeft);
      const φ = Math.PI + (t / cornerLength) * (Math.PI / 2);
      const cx = -w / 2 + rx;
      const cy = -h / 2 + ry;
      localX = cx + rx * Math.cos(φ);
      localY = cy + ry * Math.sin(φ);
      tangentAngle = φ + Math.PI / 2;
    }

    const svgX = localX + w / 2;
    const svgY = localY + h / 2;

    const advW = charWidths[i];
    const baselineY = isBottomHalf ? descenderOffset : ascenderOffset;
    const glyphPath = font.getPath(chars[i], -advW / 2, baselineY, fontSizePx);
    const d = glyphPath.toPathData(2);

    let angleDeg = tangentAngle * 180 / Math.PI;
    if (isBottomHalf) angleDeg += 180;

    pathEls.push(
      `<g transform="translate(${svgX.toFixed(2)},${svgY.toFixed(2)}) rotate(${angleDeg.toFixed(2)})">`
      + `<path d="${d}" fill="rgba(0,0,0,0.3)"/></g>`
    );
  }

  // Add padding so characters placed on the perimeter aren't clipped by the viewBox edge.
  const pad = Math.ceil(fontSizePx);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${-pad} ${-pad} ${w + 2 * pad} ${h + 2 * pad}">${pathEls.join('')}</svg>`;
}

function generateMixedTextSVG(segments, rectWidth, rectHeight, rx, ry, lineSpacing) {
  if (lineSpacing === undefined) lineSpacing = 12;
  let scaleFactor = 1.0;

  const measureSeg = (seg) => {
    const sizePx = seg.fontSizePx * scaleFactor;
    const advW = seg.font.getAdvanceWidth(seg.text, sizePx);
    // Use actual glyph bounding box so spacing matches the canvas preview,
    // which uses actualBoundingBoxAscent/Descent (not the full em-square metrics).
    let ascender, descender;
    if (seg.text) {
      const bbox = seg.font.getPath(seg.text, 0, 0, sizePx).getBoundingBox();
      ascender  = bbox.y1 < 0 ? -bbox.y1 : (seg.font.ascender  / seg.font.unitsPerEm) * sizePx;
      descender = bbox.y2 > 0 ?  bbox.y2  : (-seg.font.descender / seg.font.unitsPerEm) * sizePx;
    } else {
      ascender  = (seg.font.ascender  / seg.font.unitsPerEm) * sizePx;
      descender = (-seg.font.descender / seg.font.unitsPerEm) * sizePx;
    }
    return { seg, sizePx, advW, ascender, descender, lineH: ascender + descender };
  };

  const layout = () => segments.map(s => measureSeg(s));

  const totalH = (lines) =>
    lines.reduce((sum, m) => sum + m.lineH, 0) + lineSpacing * (lines.length - 1);

  const tooLarge = (lines) =>
    totalH(lines) > rectHeight || lines.some(m => m.advW > rectWidth);

  let lines = layout();
  while (tooLarge(lines) && scaleFactor > 0.05) {
    scaleFactor *= 0.95;
    lines = layout();
  }

  const blockH = totalH(lines);
  let curY = (rectHeight - blockH) / 2;

  const pathEls = lines.map((m) => {
    const x = rectWidth / 2 - m.advW / 2;
    const baselineY = curY + m.ascender;
    const d = m.seg.font.getPath(m.seg.text, x, baselineY, m.sizePx).toPathData(2);
    curY += m.lineH + lineSpacing;
    return `<path d="${d}" fill="rgba(0,0,0,0.3)"/>`;
  }).join('');

  const clipD = buildClipPathD(rx, ry, rectWidth, rectHeight);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${rectWidth} ${rectHeight}">
  <defs><clipPath id="c"><path d="${clipD}"/></clipPath></defs>
  <g clip-path="url(#c)">${pathEls}</g>
</svg>`;
}

// ─────────────────────────────────────────────────────────────────────────────

document.addEventListener("DOMContentLoaded", function () {
  window.addEventListener("resize", function () {
    document.documentElement.style.setProperty(
      "--window-height",
      window.innerHeight + "px"
    );
  });
});

document.addEventListener("DOMContentLoaded", () => {
  const configuratorTriggers = document.querySelectorAll(".show-configurator");
  const configuratorModal = document.querySelector(
    ".product-configurator-modal"
  );

  if (configuratorTriggers && configuratorModal) {
    configuratorTriggers.forEach(el => {
	el.addEventListener("click", (e) => {
				e.preventDefault()
				document.body.classList.add("no-scroll");
				configuratorModal.classList.add("active");
			});

		})  }
});


class MonogramPositionDetails extends HTMLElement {
  constructor() {
    super();

    this.select = this.querySelector(".monogram-option-select");

    // Hook up select → block updates
    if (this.select) {
      this.select.addEventListener("change", () => this.updateBlocks());
      this.updateBlocks(); // initial state
    }

    // Attach number & emoji filter on inputs/textareas
    this.attachNumberFilter();

    // Attach allow-numbers toggle on radio buttons
    this.attachAllowNumbersToggle();
  }

  updateBlocks() {
    const value = this.select.value;

    // Hide all monogram blocks
    this.querySelectorAll(".monogram-block").forEach((block) => {
      block.style.display = "none";
    });

    // Show the selected block
    const selectedBlock = this.querySelector(`.monogram-block--${value}`);
    if (selectedBlock) {
      selectedBlock.style.display = "block";

      if (selectedBlock.dataset.optionType === 'configurator-drawing-option') {
        this.initializeDrawingPad(selectedBlock);
      }
    }
  }

  initializeDrawingPad(block) {
    let drawingPad = block.querySelector("drawing-pad");

    if (!drawingPad) {
      drawingPad = document.createElement("drawing-pad");
      block.appendChild(drawingPad);
    }

    const positionDetails = block.closest("monogram-position-details");
    try {
      const previewBox = positionDetails?.dataset.previewBox
        ? JSON.parse(positionDetails.dataset.previewBox)
        : null;
      const borderRadius = parseFloat(previewBox?.borderradius) || 0;
      const canvas = drawingPad.querySelector("canvas");
      if (canvas) {
        if (borderRadius > 0) {
          canvas.style.borderRadius = `${borderRadius}%`;
          canvas.style.overflow = "hidden";
        } else {
          canvas.style.borderRadius = "";
          canvas.style.overflow = "";
        }
      }
    } catch (e) {}

    setTimeout(() => {
      if (typeof drawingPad.resizeCanvas === "function") {
        drawingPad.resizeCanvas();
      }
    }, 10);
  }

  attachNumberFilter() {
    const fields = this.querySelectorAll('input[type="text"], textarea');
    fields.forEach((field) => {
      field.addEventListener("input", (e) => {
        const el = e.currentTarget;
        const allow = el.getAttribute("data-allow-numbers");
        let cleaned = el.value;
        // Always remove emojis (Extended Pictographic characters)
        cleaned = cleaned.replace(/\p{Extended_Pictographic}/gu, "");
        // If numbers are disallowed, also remove digits
        if (allow === "0") {
          cleaned = cleaned.replace(/[0-9]/g, "");
        }
        if (el.classList.contains('text-upper')) {
          cleaned = cleaned.toUpperCase();
        }
        if (cleaned !== el.value) {
          const pos = el.selectionStart - (el.value.length - cleaned.length);
          el.value = cleaned;
          el.setSelectionRange(pos, pos);
        }
      });
    });
  }

  attachAllowNumbersToggle() {
    this.querySelectorAll(".product-form--radio[data-allow-numbers]").forEach(
      (radioHolder) => {
        const radio = radioHolder.querySelector('input[type="radio"]');
        radio.addEventListener("change", () => {
          if (radio.checked) {
            const allow = radioHolder.getAttribute("data-allow-numbers");
            this.querySelectorAll('input[type="text"], textarea').forEach(
              (field) => {
                field.setAttribute("data-allow-numbers", allow);
                // Strip existing emojis always
                let cleaned = field.value.replace(
                  /\p{Extended_Pictographic}/gu,
                  ""
                );
                // If numbers are now disallowed, strip digits too
                if (allow === "0") {
                  cleaned = cleaned.replace(/[0-9]/g, "");
                }
                if (cleaned !== field.value) {
                  field.value = cleaned;
                }
              }
            );
          }
        });
      }
    );
  }
}

customElements.define("monogram-position-details", MonogramPositionDetails);

class ConfiguratorPreview extends HTMLElement {
  constructor() {
    super();
    this.onSlideChanged = this.onSlideChanged.bind(this);
  }

  connectedCallback() {
    this.attachToSliders();
    this.observeForSliders();
  }

  disconnectedCallback() {
    if (this.mo) this.mo.disconnect();
    if (this.sliderListeners) {
      this.sliderListeners.forEach(({ slider, handler }) =>
        slider.removeEventListener("slideChanged", handler)
      );
    }
  }

  attachToSliders() {
    const sliders = this.querySelectorAll("slider-component");
    this.sliderListeners = this.sliderListeners || [];
    sliders.forEach((slider) => {
      if (this.sliderListeners.some((s) => s.slider === slider)) return;
      slider.addEventListener("slideChanged", this.onSlideChanged);
      this.sliderListeners.push({ slider, handler: this.onSlideChanged });
    });
  }

  observeForSliders() {
    this.mo = new MutationObserver(() => this.attachToSliders());
    this.mo.observe(this, { childList: true, subtree: true });
  }

  onSlideChanged(e) {
    const slider = e.currentTarget; // the <slider-component> that fired
    const { currentPage, currentElement } = e.detail || {};
    ////console.log("[Preview] slideChanged:", { currentPage, currentElement });

    // 1) Resolve the *correct* dot inside THIS slider
    let link =
      slider.querySelector(".slider-counter__link--active") ||
      (currentPage
        ? slider.querySelector(`.slider-counter__link[data-slide-id="${currentPage}"]`)
        : null);

    if (!link) {
      const links = [...slider.querySelectorAll(".slider-counter__link")];
      link = links[(currentPage || 1) - 1] || links[0] || null;
    }

    if (!link) {
      console.warn("[Preview] No dot/link found for current slide");
      return;
    }

    const slideId = link.getAttribute("data-slide-id") || currentPage;
    const positionName = link.getAttribute("data-position-name");
    ////console.log("[Preview] resolved link:", { slideId, positionName, link });

    if (!positionName) {
      console.warn("[Preview] link has no data-position-name");
      return;
    }

    // 2) Activate matching <monogram-position-details> in the form
    const formEl = document.querySelector("configurator-form");
    const holder = document.querySelector(".content-holder");
    if (!formEl || !holder) {
      console.warn("[Preview] Missing configurator-form or .content-holder");
      return;
    }

    const target = formEl.querySelector(
      `monogram-position-details[data-name="${CSS.escape(positionName)}"]`
    );
    if (!target) {
      console.warn("[Preview] No details for data-name:", positionName);
      return;
    }

    formEl.querySelectorAll("monogram-position-details").forEach((el) => el.classList.remove("active"));
    target.classList.add("active");

    // 3) Smooth-scroll target to center
    const holderRect = holder.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const newTop =
      holder.scrollTop +
      (targetRect.top - holderRect.top) -
      holder.clientHeight / 2 +
      target.clientHeight / 2;

    holder.scrollTo({ top: Math.max(0, newTop), behavior: "smooth" });
    //console.log("[Preview] scrolled to:", Math.max(0, newTop));

    // 4) Dispatch monogramChanged
    formEl.dispatchEvent(
      new CustomEvent("monogramChanged", {
        detail: { selectedValue: positionName },
        bubbles: true,
        composed: true,
      })
    );
    //console.log("[Preview] monogramChanged dispatched:", positionName);
  }
}

customElements.define("configurator-preview", ConfiguratorPreview);

class DrawingPad extends HTMLElement {
  constructor() {
    super();
    this.drawingRatio = null;
  }

  setDrawingRatio(ratio) {
    this.drawingRatio = ratio;
    this.resizeCanvas(true);
  }

  connectedCallback() {
    // grab elements
    this.canvas        = this.querySelector("canvas");
    this.undoButton    = this.querySelector("#undoDrawing");
    this.clearButton   = this.querySelector("#clearDrawing");
    this.saveSvgButton = this.querySelector("#saveSvg"); // optional

    // set resolution & init
    this.scaleCanvas();
    this.signaturePad = new SignaturePad(this.canvas, {
      minWidth: 2,
      maxWidth: 2,
      penColor: "black",
    });

    // watch for show/hide and window resize
    this.observeVisibility();
    window.addEventListener("resize", () => this.resizeCanvas(true));

    // Undo last stroke
    if (this.undoButton) {
      this.undoButton.addEventListener("click", () => {
        const data = this.signaturePad.toData();
        if (data.length) {
          data.pop();
          this.signaturePad.fromData(data);
          if (data.length === 0) this.classList.remove("completed");
          if (typeof this.signaturePad.onEnd === "function") {
            this.signaturePad.onEnd();
          }
        }
      });
    }

    // Clear all strokes
    if (this.clearButton) {
      this.clearButton.addEventListener("click", () => {
        this.signaturePad.clear();
        this.classList.remove("completed");
        if (typeof this.signaturePad.onEnd === "function") {
          this.signaturePad.onEnd();
        }
      });
    }

    // Download as .svg file (if you have a <button id="saveSvg">Save SVG</button>)
    if (this.saveSvgButton) {
      this.saveSvgButton.addEventListener("click", () => {
        // get raw SVG markup (with your pen paths)
        const svgString = this.signaturePad.toSVG();
        const blob      = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        const url       = URL.createObjectURL(blob);
        const a         = document.createElement("a");
        a.href          = url;
        a.download      = "drawing.svg";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
    }

    // When user finishes a stroke, emit SVG data-URL
    this.signaturePad.onEnd = () => {
      this.classList.add("completed");
      // produce a data:image/svg+xml;base64,… string
      const svgDataUrl = this.signaturePad.toDataURL("image/svg+xml");
      this.dispatchEvent(
        new CustomEvent("drawingFinished", {
          detail: { svgDataUrl },
          bubbles: true,
          composed: true,
        })
      );
    };
  }

  scaleCanvas() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    // save existing drawing
    const savedData = this.signaturePad ? this.signaturePad.toData() : null;
    // resize
    this.canvas.width = this.clientWidth * ratio;
    if (this.drawingRatio) {
      this.canvas.height = (this.clientWidth / this.drawingRatio) * ratio;
    } else {
      this.canvas.height = this.clientHeight * ratio;
    }
    this.canvas.getContext("2d").scale(ratio, ratio);
    // restore
    if (this.signaturePad && savedData) {
      this.signaturePad.fromData(savedData);
    }
  }

  resizeCanvas(preserveDrawing = false) {
    if (this.offsetWidth > 0 && this.offsetHeight > 0) {
      this.scaleCanvas();
    }
  }

  observeVisibility() {
    const observer = new MutationObserver(() => {
      if (this.offsetWidth > 0 && this.offsetHeight > 0) {
        this.resizeCanvas(true);
      }
    });
    observer.observe(this, { attributes: true, attributeFilter: ["style", "class"] });
  }
}

customElements.define("drawing-pad", DrawingPad);

class ImageUpload extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.fileInput = this.querySelector(".image-upload-input");
    this.fileNameDisplay = this.querySelector(".image-upload-filename");
    this.clearButton = this.querySelector("#clear");

    if (this.fileInput) {
      this.fileInput.addEventListener("change", (event) =>
        this.handleFileChange(event)
      );
    }

    if (this.clearButton) {
      this.clearButton.addEventListener("click", () => this.clearInput());
    }
  }

  handleFileChange(event) {
    const file = event.target.files[0];
    if (file) {
      this.fileNameDisplay.textContent = file.name.toUpperCase();
    }
  }

  clearInput() {
    if (this.fileInput) {
      // Clear the file input's value
      this.fileInput.value = "";

      // Reset the filename display to its data-default attribute value or an empty string if not provided
      const defaultText =
        this.fileNameDisplay.getAttribute("data-default") || "";
      this.fileNameDisplay.textContent = defaultText;

      // Create and dispatch a change event so that other scripts are notified
      const changeEvent = new Event("change", { bubbles: true });
      this.fileInput.dispatchEvent(changeEvent);
    }
  }
}

customElements.define("image-upload", ImageUpload);

class RangeInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.rangeInput = this.querySelector('input[type="range"]');
    this.valueDisplay = this.querySelector('.value-display');
    this.unit = this.dataset.unit || '';

    if (this.rangeInput && this.valueDisplay) {
      this.updateValueDisplay(this.rangeInput.value);
      this.rangeInput.addEventListener('input', (event) => {
        //console.log('range input changed');
        this.updateValueDisplay(event.target.value);
      });
    }
  }

  updateValueDisplay(value) {
    this.valueDisplay.textContent = `${value}${this.unit}`;
  }
}

customElements.define("range-input", RangeInput);

class PreviewGenerator extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const inputs = this.querySelectorAll('input');

    inputs.forEach((input) => {
      if (input.type !== 'image') {
        input.addEventListener('input', () => this.updateSummary());
      }
    });

    // Initialize summary on load if needed
    this.updateSummary();
  }

  updateSummary() {
    const inputs = this.querySelectorAll('input:not(.skip-loop)');
    const summaryObject = {}; // Change to object

    inputs.forEach((input) => {
      if (input.type === 'image') return;

      const name = input.name?.toLowerCase() || input.id?.toLowerCase();
      if (!name) return;

      let value = input.value; // Keep original value for type conversion
      const unit = input.dataset.unit || '';

      if (input.type === 'checkbox') {
        value = input.checked; // Boolean for checkboxes
      } else if (!isNaN(value) && value !== '') { // Convert to number if it's a number
        value = parseFloat(value);
      } else {
        value = value.toLowerCase(); // Keep as string, lowercase
      }

      // Special handling for percentage values like width, height, top, left, borderradius, rotate
      if (['width', 'height', 'top', 'left', 'borderradius', 'rotate'].includes(name)) {
        summaryObject[name] = `${value}${unit}`;
      } else {
        summaryObject[name] = value;
      }
    });

    const resultJSON = JSON.stringify(summaryObject, null, 2); // Pretty print for readability
    //console.log(resultJSON);
    
    const parentDetails = this.closest('monogram-position-details');
    
    if (parentDetails) {
      parentDetails.dataset.previewBox = resultJSON; // Store JSON string

      // Get #PositionName input value
      const nameInput = this.querySelector('#PositionName');
      if (nameInput && nameInput.value.trim() !== '') {
        const rawValue = nameInput.value.trim();
        parentDetails.dataset.title = rawValue;
        // Convert to handle format (matches Liquid's | handle filter)
        parentDetails.dataset.name = rawValue.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
    }
     // 🔔 Find the closest <configurator-form> and dispatch the event from it

const testInput = this.querySelector('#TestText');
    
         var testValue = '';
    
      if (testInput && testInput.value.trim() !== '') {
         testValue = testInput.value;
      }

		var output = this.querySelector('#finalMetafieldString')
		if(output) output.textContent = resultJSON
    
    const configuratorForm = this.closest("configurator-form");
    if (configuratorForm) {
      configuratorForm.dispatchEvent(
        new CustomEvent("monogramChanged", {
          detail: "variantChange",
          bubbles: true,
          composed: true,
        })
      );
    }

  }
}

customElements.define("preview-generator", PreviewGenerator);

class TypeSettingsGenerator extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('input', () => this.updateSummary());
    });
    const typeSelect = this.parentElement?.querySelector('#SetupOptionType');
    if (typeSelect) {
      typeSelect.addEventListener('change', () => {
        this.updateVisibility(typeSelect.value);
        this.updateSummary();
      });
    }
  }

  updateVisibility(type) {
    this.querySelectorAll('[data-for-types]').forEach(el => {
      const types = el.dataset.forTypes.split(',');
      el.style.display = types.includes(type) ? '' : 'none';
    });
    const posDetails = this.closest('monogram-position-details');
    if (posDetails) posDetails.dataset.setupType = type;

    // Prefill inputs from saved settings if a preset exists for this type
    const preset = this.querySelector(`.setup-option-preset[data-type="${type}"]`);
    if (preset) {
      try {
        this.prefillFromSettings(JSON.parse(preset.dataset.settings || '{}'));
      } catch(e) {}
    }

    // Apply default test text and sync maxlength
    this.syncTestInputs(type);
  }

  prefillFromSettings(settings) {
    this.querySelectorAll('input:not(.skip-loop)').forEach(input => {
      const name = input.name?.toLowerCase();
      if (!name || !(name in settings)) return;
      if (input.type === 'checkbox') {
        input.checked = settings[name] === true;
      } else {
        input.value = settings[name];
      }
      // Notify range-input components to update their value display
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  syncTestInputs(type) {
    const isText     = type === 'configurator_text_option' || type === 'configurator_icon_text_option';
    const isTwoText  = type === 'configurator_two_text_option';

    if (isText) {
      const maxLengthInput = this.querySelector('#TypeMaxLength');
      const testInput = this.querySelector('#SetupTestText');
      if (testInput) {
        const max = parseInt(maxLengthInput?.value, 10) || 50;
        testInput.maxLength = max;
        if (!testInput.value) testInput.value = 'Sample text'.slice(0, max);
      }
    }

    if (isTwoText) {
      const ml1Input = this.querySelector('#TypeMaxLength1');
      const test1 = this.querySelector('#SetupTestText1');
      if (test1) {
        const max1 = parseInt(ml1Input?.value, 10) || 50;
        test1.maxLength = max1;
        if (!test1.value) test1.value = 'Top line'.slice(0, max1);
      }

      const ml2Input = this.querySelector('#TypeMaxLength2');
      const test2 = this.querySelector('#SetupTestText2');
      if (test2) {
        const max2 = parseInt(ml2Input?.value, 10) || 50;
        test2.maxLength = max2;
        if (!test2.value) test2.value = 'Bottom'.slice(0, max2);
      }
    }
  }

  updateSummary() {
    const typeSelect = this.parentElement?.querySelector('#SetupOptionType');
    const type = typeSelect?.value || '';
    const posDetails = this.closest('monogram-position-details');
    if (posDetails) posDetails.dataset.setupType = type;

    if (!type) {
      const output = this.querySelector('#typeSettingsString');
      if (output) output.textContent = '';
      if (posDetails) posDetails.dataset.typeSettings = '';
      return;
    }

    const settings = {};
    this.querySelectorAll('input:not(.skip-loop)').forEach(input => {
      const typedParent = input.closest('[data-for-types]');
      if (typedParent && typedParent.style.display === 'none') return;
      const name = input.name?.toLowerCase();
      if (!name) return;
      if (input.type === 'checkbox') {
        settings[name] = input.checked;
      } else if (input.value !== '' && !isNaN(input.value)) {
        settings[name] = parseFloat(input.value);
      } else {
        settings[name] = input.value;
      }
    });

    const json = JSON.stringify(settings, null, 2);
    const output = this.querySelector('#typeSettingsString');
    if (output) output.textContent = json;
    if (posDetails) posDetails.dataset.typeSettings = json;

    // Keep test input maxlength in sync as sliders change
    this.syncTestInputs(type);

    const configuratorForm = this.closest('configurator-form');
    if (configuratorForm) {
      configuratorForm.dispatchEvent(new CustomEvent('monogramChanged', {
        detail: 'variantChange', bubbles: true, composed: true
      }));
    }
  }
}
customElements.define('type-settings-generator', TypeSettingsGenerator);

class ConfiguratorForm extends HTMLElement {
  constructor() {
    super();
    // Track crossing states per element
    this.midlineStates = new WeakMap();
    this.scrollRaf = null;
    this.lastScrollTop = 0;
    this.scrollDir = "down";

    this.initEvents();
    this.initScrollWatcher();
  }

  initEvents() {
    // --- Existing event listeners ---
    this.monogramSelects = this.querySelectorAll(".monogram-option-select");
    this.monogramSelects.forEach((select) => {
      select.addEventListener("change", (event) => {
        this.updateMonogramDetails(event);
        // Show clear button only when a content-input type is selected.
        // Drawing and image have their own clear buttons; no-selection hides it too.
        const posEl = event.target.closest('monogram-position-details');
        const clearBtn = posEl?.querySelector('.button--clear-position');
        if (clearBtn) {
          const val = event.target.value.replace(/_/g, '-');
          const block = posEl.querySelector('.monogram-block--' + val);
          const optType = block?.dataset.optionType || '';
          clearBtn.style.display = (optType && !optType.includes('drawing') && !optType.includes('image')) ? '' : 'none';
        }
      });
      // Set initial visibility on load.
      const posEl = select.closest('monogram-position-details');
      const clearBtn = posEl?.querySelector('.button--clear-position');
      if (clearBtn) {
        const val = select.value.replace(/_/g, '-');
        const block = posEl.querySelector('.monogram-block--' + val);
        const optType = block?.dataset.optionType || '';
        clearBtn.style.display = (optType && !optType.includes('drawing') && !optType.includes('image')) ? '' : 'none';
      }
    });

    // Clear button: reset content inputs in the active block and redraw canvas.
    this.addEventListener('click', (event) => {
      if (!event.target.matches('.button--clear-position')) return;
      const posEl = event.target.closest('monogram-position-details');
      if (!posEl) return;
      const select = posEl.querySelector('.monogram-option-select');
      const blockType = select?.value.replace(/_/g, '-') || '';
      const activeBlock = posEl.querySelector('.monogram-block--' + blockType);
      if (!activeBlock) return;
      // Clear text content inputs (not font/size controls).
      activeBlock.querySelectorAll(
        'input[type="text"].field__input, textarea.field__input, .field__input--text-1, .field__input--text-2'
      ).forEach(el => {
        el.value = '';
        el.dispatchEvent(new Event('input', { bubbles: true }));
      });
      // Uncheck icon selection radios (not font or orientation radios).
      activeBlock.querySelectorAll(
        '.font-icons-holder input[type="radio"], .icons-holder input[type="radio"]'
      ).forEach(radio => { radio.checked = false; });
      // Redraw canvas.
      this.dispatchEvent(new CustomEvent('monogramChanged', {
        bubbles: true, composed: true, detail: { selectedValue: 'clear', source: 'clearButton' }
      }));
    });

    // Prevent adding characters to text inputs when the font is at its minimum size.
    this.addEventListener('beforeinput', (event) => {
      const input = event.target;
      if (!input.matches('input[type="text"].field__input, textarea.field__input')) return;
      if (input.dataset.atLimit !== 'true') return;
      const deletionTypes = [
        'deleteContentBackward', 'deleteContentForward',
        'deleteWordBackward', 'deleteWordForward',
        'deleteSoftLineBackward', 'deleteSoftLineForward',
        'deleteEntireSoftLine', 'deleteHardLineBackward', 'deleteHardLineForward',
        'deleteByDrag', 'deleteByCut',
      ];
      if (!deletionTypes.includes(event.inputType)) {
        event.preventDefault();
        input.reportValidity();
      }
    }, true);

    this.fontRadios = this.querySelectorAll(
      "monogram-position-details .product-form--radio input[type='radio']"
    );
    this.fontRadios.forEach((radio) => {
      radio.addEventListener("change", (event) => this.updateMonogramDetails(event));
    });

    this.letterInputs = this.querySelectorAll(".field__input");
    this.letterInputs.forEach((input) => {
      input.addEventListener("input", (event) => this.updateMonogramDetails(event));
    });

    this.imageUploads = this.querySelectorAll(".image-upload-input");
    this.imageUploads.forEach((input) => {
      input.addEventListener("change", (event) => this.updateMonogramDetails(event));
    });

    this.drawingPads = this.querySelectorAll("drawing-pad");
    this.drawingPads.forEach((pad) => {
      pad.addEventListener("drawingFinished", (event) => this.updateMonogramDetails(event));
    });

    // --- New listeners for two-way sync ---

    // 1. Get all variant data from the script tag in the new snippet
    const variantDataScript = this.querySelector('.product-options-select script[type="application/json"]');
    if (variantDataScript) {
      this.productVariants = JSON.parse(variantDataScript.textContent);
    } else {
      console.error('Configurator: Variant JSON data not found!');
    }

    // 2. Find the main product page's variant radios element
    this.mainVariantRadios = document.querySelector('variant-radios');

    // 3. Find the configurator's color swatches
    this.configuratorColorSwatches = this.querySelectorAll('.product-options-select input[name^="configurator-"]');

    // 4. Listen for changes coming FROM the configurator's color swatches
    this.configuratorColorSwatches.forEach(swatch => {
      swatch.addEventListener('change', this.onConfiguratorColorChange.bind(this));
    });

    // --- Single, combined listener for all variant changes (main form -> configurator) ---
    document.body.addEventListener("variantChange", this.handleVariantChange.bind(this));

    this.plusButtons = this.querySelectorAll('button[name="plus"]');
    this.minusButtons = this.querySelectorAll('button[name="minus"]');

    this.plusButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        this.updateMonogramDetails(event);
      });
    });

    this.minusButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        this.updateMonogramDetails(event);
      });
    });
  }

	onConfiguratorColorChange(e){
		var target = e.target

		var value = e.target?.value

		if(!value) return

		var main_radio = this.mainVariantRadios.querySelector(`input[value="${value}"]`)

		if(!main_radio) return

		main_radio.checked = true

		this.mainVariantRadios.dispatchEvent(new Event('change', { bubbles: true }));
	}

  initScrollWatcher() {
    this.contentHolder = this.querySelector(".content-holder");
    if (!this.contentHolder) return;

    this.getDetails = () =>
      Array.from(this.querySelectorAll("monogram-position-details"));

    this.lastScrollTop = this.contentHolder.scrollTop;
    this.scrollDir = "down";

    this.onScroll = () => {
      const st = this.contentHolder.scrollTop;
      this.scrollDir = st < this.lastScrollTop ? "up" : "down";
      this.lastScrollTop = st;

      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.checkMidlineCrossings();
      });
    };

    this.contentHolder.addEventListener("scroll", this.onScroll, {
      passive: true,
    });

    this.onResize = () => this.checkMidlineCrossings();
    window.addEventListener("resize", this.onResize, { passive: true });

    this.checkMidlineCrossings();
  }

  checkMidlineCrossings() {
    if (!this.contentHolder) return;

    const holderRect = this.contentHolder.getBoundingClientRect();
    const midY = holderRect.top + holderRect.height / 2;

    const detailsList = this.getDetails ? this.getDetails() : [];
    detailsList.forEach((el) => {
      const r = el.getBoundingClientRect();

      const topAtOrAboveMid = r.top <= midY;
      const bottomAtOrBelowMid = r.bottom >= midY;

      const last = this.midlineStates.get(el);
      if (!last) {
        this.midlineStates.set(el, { topAtOrAboveMid, bottomAtOrBelowMid });
        return;
      }

      // Scroll down → top crosses middle
      if (
        this.scrollDir === "down" &&
        last.topAtOrAboveMid === false &&
        topAtOrAboveMid === true
      ) {
        const name =
          el.getAttribute("data-name") || el.dataset.name || "(no data-name)";
        //console.log(name);
        this.activateByScroll(el, name, "topCrossDown");
      }

      // Scroll up → bottom crosses middle
      if (
        this.scrollDir === "up" &&
        last.bottomAtOrBelowMid === false &&
        bottomAtOrBelowMid === true
      ) {
        const name =
          el.getAttribute("data-name") || el.dataset.name || "(no data-name)";
        //console.log(name);
        this.activateByScroll(el, name, "bottomCrossUp");
      }

      this.midlineStates.set(el, { topAtOrAboveMid, bottomAtOrBelowMid });
    });
  }

  activateByScroll(el, selectedValue, trigger) {
    this.querySelectorAll("monogram-position-details").forEach((d) =>
      d.classList.remove("active")
    );

    el.classList.add("active");

    this.dispatchEvent(
      new CustomEvent("monogramChanged", {
        detail: {
          selectedValue,
          source: "scrollCross",
          trigger,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  updateColourValue(newVariant) {
    let product_form = document.querySelector(".product");
    let product_configurator_form = document.querySelector("configurator-modal");

    if (!product_form || !product_configurator_form) return;

    const color_index = product_form.dataset.baseColorIndex;
    let selected_color = newVariant.options[parseInt(color_index)];

    if (!selected_color) return;

    product_configurator_form.dataset.currentColor = selected_color.toLowerCase();

		//set the swatch
		var swatch_input = product_configurator_form.querySelector(`[data-value=${selected_color} i]`)
		if(!swatch_input) return

		swatch_input.checked = true
  }

  handleVariantChange(event) {

    //console.log("handleVariantChange");
    this.variantInput = this.querySelector("#configurator_variant_select");
    var newVariant = event.detail.currentVariant;
		debugger
    //console.log("newId:", newVariant.id);
    if (this.variantInput) this.variantInput.value = newVariant.id;
    this.updateColourValue(newVariant);
    this.updateMonogramDetails(event);

    this.checkMidlineCrossings();
  }

  updateMonogramDetails(event) {
    //console.log("updateMonogramDetails triggered");
    //console.log("event is: ", event);
    let selectedValue;

    if (event.type === "drawingFinished") {
      selectedValue = event.detail.imageData;
    } else if (event.type === "variantChange") {
      selectedValue = event.type;
    } else if (event.target && event.target.type === "file") {
      selectedValue =
        event.target.files.length > 0
          ? event.target.files[0].name
          : "No file selected";
    } else {
      selectedValue =
        event.target?.value ||
        event.target?.closest(".product-form--radio")?.dataset.font;
    }

    if (
      event.target?.matches?.('input[type="radio"]') &&
      event.target.hasAttribute("data-title")
    ) {
      const targetTitle = event.target.getAttribute("data-title");
      const fieldset = event.target.closest("fieldset");
      if (fieldset) {
        const iconValueEl = fieldset.querySelector(".icon-title-holder .icon-value");
        if (iconValueEl) {
          iconValueEl.textContent = targetTitle;
        } else {
          //console.log("No .icon-title-holder .icon-value inside this fieldset");
        }
      }
    }

    /*console.log(
      "Selected Monogram Type, Font, Letter, Image, or Drawing:",
      selectedValue
    );*/

    if (event.type !== "variantChange") {
      this.querySelectorAll("monogram-position-details").forEach((el) =>
        el.classList.remove("active")
      );
    }

    const parentMonogramDetails =
      event.target?.closest?.("monogram-position-details");
    if (parentMonogramDetails) {
      parentMonogramDetails.classList.add("active");
    }

    this.dispatchEvent(
      new CustomEvent("monogramChanged", {
        detail: { selectedValue, source: "uiChange" },
        bubbles: true,
        composed: true,
      })
    );
  }

  onUnload() {
    if (this.contentHolder && this.onScroll) {
      this.contentHolder.removeEventListener("scroll", this.onScroll);
    }
    if (this.onResize) {
      window.removeEventListener("resize", this.onResize);
    }
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }
  }
}

customElements.define("configurator-form", ConfiguratorForm);


class ConfiguratorModal extends HTMLElement {
  constructor() {
    super();

    // Add event listener for the preview button
    this.previewButton = this.querySelector(".previewButton");
    if (this.previewButton) {
      this.previewButton.addEventListener("click", (event) =>
        this.previewButtonClicked(event)
      );
    }

this.flipButton = this.querySelector(".flip-preview");
		var canvas_holder = this.flipButton?.closest(".canvas-holder")

if (this.flipButton && canvas_holder) {
  this.flipButton.addEventListener("click", (event) => {
		canvas_holder.classList.toggle("flipped")
  });
}


    // Select all elements with the class 'button--back'
    this.returnButtons = this.querySelectorAll(".button--back");

    // Loop through each element and attach the event listener
    this.returnButtons.forEach((button) => {
      button.addEventListener("click", (event) =>
        this.returnButtonClicked(event)
      );
    });

    // this.toggleFinalPreviews();

    this.productColourSelects = this.querySelectorAll(
      ".product-options-select input[type='radio']"
    );
    // this.productColourSelects.forEach((select) => {
    //   select.addEventListener("change", (event) =>
    //     this.toggleFinalPreviews(event)
    //   );
    // });

    const modalCloseButton = this.querySelector(".modal__close");

    if (modalCloseButton) {
      modalCloseButton.addEventListener("click", () => {
        this.classList.remove("active");
        document.body.classList.remove("no-scroll");
      });
    }

    this.addEventListener("click", (event) => {
      // Only remove the active class if the overlay itself was clicked (not any of its children)
      if (event.target === this) {
        this.classList.remove("active");
        document.body.classList.remove("no-scroll");
      }
    });

     const openBtns = document.querySelectorAll('.openImagePopup');
  const popup    = document.querySelector('.image-popup');
  const closeBtn = popup.querySelector('.popup-close');

  // open popup when any .openImagePopup is clicked
  openBtns.forEach(btn =>
    btn.addEventListener('click', () => {
      popup.classList.add('active');
    })
  );

  // close popup
  closeBtn.addEventListener('click', () => {
    popup.classList.remove('active');
  });

  // ─── Corner-picker (setup mode only) ───
  this.initCornerPicker();
  this.initSetupTabs();
  }

  initSetupTabs() {
    const tabButtons = this.querySelectorAll('.setup-tab-button');
    if (!tabButtons.length) return;

    tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const targetName = btn.dataset.targetPosition;

        // Toggle active tab button
        tabButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // Show/hide canvases
        this.querySelectorAll('.setup-position-canvas').forEach(wrapper => {
          wrapper.classList.toggle('hidden', wrapper.dataset.positionName !== targetName);
        });

        // Show/hide monogram-position-details panels
        this.querySelectorAll('configurator-form monogram-position-details').forEach(detail => {
          if (detail.dataset.name === targetName) {
            detail.classList.add('active');
            detail.style.display = '';
          } else {
            detail.classList.remove('active');
            detail.style.display = 'none';
          }
        });

        // Exit corner mode if active, then redraw
        if (this.cornerPickerActive) this.exitCornerMode();
        this.updateAllCanvas();
      });
    });
  }

  initCornerPicker() {
    const setupPreview = this.querySelector('configurator-preview[data-setup-active]');
    if (!setupPreview) return;

    this.cornerPickerActive = false;
    this.cornerPoints = [];
    this._cornerCanvasHandler = null;
    this._cornerActiveCanvas = null;
    this._cornerActiveBtn = null;
    this._cornerActiveStatus = null;

    const cornerLabels = [
      'Click top-left corner',
      'Click top-right corner',
      'Click bottom-right corner',
      'Click bottom-left corner',
    ];

    // Use event delegation for #setCorners buttons (one per position)
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('#setCorners');
      if (!btn) return;

      if (this.cornerPickerActive) {
        this.exitCornerMode();
        return;
      }

      // Find the active canvas
      const canvas = this.getActiveSetupCanvas();
      if (!canvas) return;

      const activeDetail = this.querySelector('configurator-form monogram-position-details.active');
      const status = activeDetail ? activeDetail.querySelector('.corner-picker-status') : null;

      // Enter corner-picking mode
      this.cornerPickerActive = true;
      this.cornerPoints = [];
      this._cornerActiveCanvas = canvas;
      this._cornerActiveBtn = btn;
      this._cornerActiveStatus = status;

      btn.textContent = 'Cancel';
      if (status) {
        status.textContent = cornerLabels[0];
        status.style.display = '';
      }
      canvas.style.cursor = 'crosshair';
      // Redraw canvas to clear any old markers
      this.updateAllCanvas();

      // Create and store canvas click handler
      this._cornerCanvasHandler = (ev) => {
        if (!this.cornerPickerActive) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (ev.clientX - rect.left) * scaleX;
        const y = (ev.clientY - rect.top) * scaleY;

        this.cornerPoints.push({ x, y });

        // Draw marker
        const ctx = canvas.getContext('2d');
        const colors = ['#ff0000', '#00cc00', '#0066ff', '#ff9900'];
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, Math.PI * 2);
        ctx.fillStyle = colors[this.cornerPoints.length - 1] || '#ff0000';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();

        if (this.cornerPoints.length < 4) {
          if (status) status.textContent = cornerLabels[this.cornerPoints.length];
        } else {
          // 4 corners collected — compute and apply
          this.computeAndApplyRectangle(this.cornerPoints, canvas);
          this.exitCornerMode();
        }
      };

      canvas.addEventListener('click', this._cornerCanvasHandler);
    });
  }

  getActiveSetupCanvas() {
    const activeDetail = this.querySelector('configurator-form monogram-position-details.active');
    if (!activeDetail) return null;
    const posName = activeDetail.dataset.name;
    const wrapper = this.querySelector(`.setup-position-canvas[data-position-name="${posName}"]`);
    return wrapper ? wrapper.querySelector('canvas') : null;
  }

  exitCornerMode() {
    this.cornerPickerActive = false;
    this.cornerPoints = [];

    if (this._cornerActiveBtn) {
      this._cornerActiveBtn.textContent = 'Set Corners';
    }
    if (this._cornerActiveStatus) {
      this._cornerActiveStatus.style.display = 'none';
    }
    if (this._cornerActiveCanvas) {
      this._cornerActiveCanvas.style.cursor = '';
      if (this._cornerCanvasHandler) {
        this._cornerActiveCanvas.removeEventListener('click', this._cornerCanvasHandler);
      }
    }

    this._cornerCanvasHandler = null;
    this._cornerActiveCanvas = null;
    this._cornerActiveBtn = null;
    this._cornerActiveStatus = null;

    // Redraw canvas to clear markers
    this.updateAllCanvas();
  }

  computeAndApplyRectangle(points, canvas) {
    const [p0, p1, p2, p3] = points; // TL, TR, BR, BL

    const dist = (a, b) => Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);

    const rotationRad = Math.atan2(p1.y - p0.y, p1.x - p0.x);
    const widthPx = dist(p0, p1);
    const heightPx = (dist(p0, p3) + dist(p1, p2)) / 2;

    const leftPct = (p0.x / canvas.width) * 100;
    const topPct = (p0.y / canvas.height) * 100;
    const widthPct = (widthPx / canvas.width) * 100;
    const heightPct = (heightPx / canvas.height) * 100;
    const rotationDeg = rotationRad * (180 / Math.PI);

    const setInput = (id, value) => {
      const activePosition = this.querySelector('monogram-position-details.active');
      const scope = activePosition || this;
      const input = scope.querySelector('#' + id);
      if (!input) return;
      const min = parseFloat(input.min);
      const max = parseFloat(input.max);
      let clamped = Math.round(value);
      if (!isNaN(min)) clamped = Math.max(min, clamped);
      if (!isNaN(max)) clamped = Math.min(max, clamped);
      input.value = clamped;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    setInput('Width', widthPct);
    setInput('Height', heightPct);
    setInput('top', topPct);
    setInput('left', leftPct);
    setInput('Rotate', rotationDeg);
  }

  connectedCallback() {
    // Only attempt state restoration if there is actually saved state.
    // On a fresh page load data-savedState is absent; setting _stateRestored=true
    // prevents restoreConfiguratorState from being called on the user's first
    // keypress (which would overwrite their input with the empty state that
    // window.load→saveConfiguratorState had serialised).
    // After a combined-listing swap the cached outerHTML carries the saved state
    // attribute, so _stateRestored=false and restoration runs as intended.
    this._stateRestored = !this.dataset.savedState;

    document.addEventListener("monogramChanged", (event) => {
      // After a combined-listing swap _stateRestored is false (saved state exists).
      // Restore once, then set the flag so subsequent events skip this block.
      if (!this._stateRestored) {
        this._stateRestored = true;
        this.restoreConfiguratorState();
      }

      // When there is no active position (e.g. after a combined-listing swap
      // reinitialises the modal) fall back to rendering all canvases directly.
      if (this.querySelector("monogram-position-details.active")) {
        this.updateAllCanvas(event);
      } else {
        this.querySelectorAll("configurator-preview .final-preview-position")
          .forEach((canvas) => this.updateMonogram(canvas));
      }
    });
    // Defer initial canvas render until all @font-face fonts (including the icon
    // font) have finished loading. Without this, browser-remembered form state
    // can cause the canvas to draw before the icon font is available, showing a
    // fallback system-font character instead of the correct icon glyph.
    window.addEventListener("load", () => {
      document.fonts.ready.then(() => {
        this.querySelectorAll("configurator-preview .final-preview-position")
          .forEach((canvas) => this.updateMonogram(canvas));
      });
    });

    // Eagerly preload icon font for SVG generation (opentype.js path).
    {
      const iconFontName = this.dataset.iconFontName;
      const iconFontUrl  = this.dataset.iconFontUrl;
      if (iconFontName && iconFontUrl) fontLoader.load(iconFontName, iconFontUrl);
    }

    const configuratorForm = this.querySelector(".configurator-product-form");
    if (configuratorForm) {
      configuratorForm.addEventListener("submit", (event) => {
        event.preventDefault();
        //console.log("Form submitted!");
        this.formSubmitIntent();
      });
    }
  }

  formSubmitIntent() {
    const consentCheckbox = this.querySelector("#configurator-consent");
    if (consentCheckbox.checked) {
      this.querySelector("#configurator-consent-label").classList.remove(
        "error"
      );

      this.querySelector(".product-form__submit").classList.add("disabled");
      this.querySelector(".product-form__submit").classList.add("loading");
            this.querySelector(".product-form__submit .loading-overlay__spinner").classList.remove("hidden");
      // Create the form element.
      const form = document.createElement("form");

      // Array to store file input data so we can append them later.
      const fileFields = [];

      // Loop through each monogram-position-details element.
      const monogramPositions = this.querySelectorAll(
        "monogram-position-details"
      );
      monogramPositions.forEach((pos) => {
        // --- Add the human-readable summary property for this position ---
        const posSelect = pos.querySelector(".monogram-option-select");
        if (posSelect) {
          const positionLabel = posSelect.dataset.positionLabel
            || posSelect.name.replace(/^properties\[/, '').replace(/\]$/, '');
          const summaryValue = this.getPositionSummaryValue(pos);
          if (summaryValue) {
            const hiddenSummary = document.createElement("input");
            hiddenSummary.type = "hidden";
            hiddenSummary.name = `properties[${positionLabel}]`;
            hiddenSummary.value = summaryValue;
            form.appendChild(hiddenSummary);
          }
        }

        // Get the selected option value.
        const posOption = posSelect ? posSelect.value : "";

        // Resolve active block and its system type
        const activeBlock = posOption ? pos.querySelector(`.monogram-block--${posOption}`) : null;
        const blockType = activeBlock?.dataset.optionType || posOption;

        // --- Process based on the selected option ---
        if (blockType === 'configurator-text-option') {
          const textValueInput = activeBlock?.querySelector('.text-value');
          const fontValueInput = activeBlock?.querySelector('.font-value');

          if (textValueInput) {
            const dataName = textValueInput.dataset.name;
            if (dataName) {
              const fieldInput = activeBlock.querySelector('.field__input');
              const hiddenText = document.createElement('input');
              hiddenText.type = 'hidden';
              hiddenText.name = dataName;
              hiddenText.value = fieldInput ? fieldInput.value : '';
              form.appendChild(hiddenText);
            }
          }
          if (fontValueInput) {
            const dataName = fontValueInput.dataset.name;
            if (dataName) {
              const checkedRadio = activeBlock.querySelector('.product-form--radio input[type="radio"]:checked');
              const hiddenFont = document.createElement('input');
              hiddenFont.type = 'hidden';
              hiddenFont.name = dataName;
              hiddenFont.value = checkedRadio?.value || activeBlock.querySelector('.product-form--radio input[type="radio"]')?.value || '';
              form.appendChild(hiddenFont);
            }
          }
        } else if (blockType === 'configurator-icon-option') {
          const iconValueInput = activeBlock?.querySelector('.icon-value');
          if (iconValueInput) {
            const dataName = iconValueInput.dataset.name;
            if (dataName) {
              const checkedRadio = activeBlock.querySelector('.product-form--radio input[type="radio"]:checked');
              const hiddenIcon = document.createElement('input');
              hiddenIcon.type = 'hidden';
              hiddenIcon.name = dataName;
              hiddenIcon.value = checkedRadio?.value || activeBlock.querySelector('.product-form--radio input[type="radio"]')?.value || '';
              form.appendChild(hiddenIcon);
            }
          }
        } else if (
          blockType === 'conigurator-icon-text-option' ||
          blockType === 'configurator-icon-text-option'
        ) {
          const textValueInput = activeBlock?.querySelector('.text-value');
          const fontValueInput = activeBlock?.querySelector('.font-value');
          const iconValueInput = activeBlock?.querySelector('.icon-value');

          if (textValueInput) {
            const dataName = textValueInput.dataset.name;
            if (dataName) {
              const fieldInput = activeBlock.querySelector('.field__input');
              const hiddenText = document.createElement('input');
              hiddenText.type = 'hidden';
              hiddenText.name = dataName;
              hiddenText.value = fieldInput ? fieldInput.value : '';
              form.appendChild(hiddenText);
            }
          }
          if (fontValueInput) {
            const dataName = fontValueInput.dataset.name;
            if (dataName) {
              const checkedRadio = activeBlock.querySelector('.fonts-holder .product-form--radio input[type="radio"]:checked');
              const hiddenFont = document.createElement('input');
              hiddenFont.type = 'hidden';
              hiddenFont.name = dataName;
              hiddenFont.value = checkedRadio?.value || activeBlock.querySelector('.fonts-holder .product-form--radio input[type="radio"]')?.value || '';
              form.appendChild(hiddenFont);
            }
          }
          if (iconValueInput) {
            const dataName = iconValueInput.dataset.name;
            if (dataName) {
              const checkedRadio = activeBlock.querySelector('.icons-holder .product-form--radio input[type="radio"]:checked');
              const hiddenIcon = document.createElement('input');
              hiddenIcon.type = 'hidden';
              hiddenIcon.name = dataName;
              hiddenIcon.value = checkedRadio?.value || activeBlock.querySelector('.icons-holder .product-form--radio input[type="radio"]')?.value || '';
              form.appendChild(hiddenIcon);
            }
          }
        } else if (blockType === 'configurator-two-icons-option') {
          const firstIconValueInput = activeBlock?.querySelector('.first-icon-value');
          const secondIconValueInput = activeBlock?.querySelector('.second-icon-value');

          if (firstIconValueInput) {
            const dataName = firstIconValueInput.dataset.name;
            if (dataName) {
              const checkedRadio = activeBlock.querySelector('.font-first-icons-holder .product-form--radio input[type="radio"]:checked');
              const hiddenFirstIcon = document.createElement('input');
              hiddenFirstIcon.type = 'hidden';
              hiddenFirstIcon.name = dataName;
              hiddenFirstIcon.value = checkedRadio?.value || activeBlock.querySelector('.font-first-icons-holder .product-form--radio input[type="radio"]')?.value || '';
              form.appendChild(hiddenFirstIcon);
            }
          }
          if (secondIconValueInput) {
            const dataName = secondIconValueInput.dataset.name;
            if (dataName) {
              const checkedRadio = activeBlock.querySelector('.font-second-icons-holder .product-form--radio input[type="radio"]:checked');
              const hiddenSecondIcon = document.createElement('input');
              hiddenSecondIcon.type = 'hidden';
              hiddenSecondIcon.name = dataName;
              hiddenSecondIcon.value = checkedRadio?.value || activeBlock.querySelector('.font-second-icons-holder .product-form--radio input[type="radio"]')?.value || '';
              form.appendChild(hiddenSecondIcon);
            }
          }
        } else if (blockType === 'configurator-two-text-option') {
          const twoTextSubmitBs = this.getBlockSettings(activeBlock);
          const text1Input = activeBlock?.querySelector('.field__input--text-1');
          const text2Input = activeBlock?.querySelector('.field__input--text-2');
          const dataName1 = activeBlock?.querySelector('.text-value-1')?.dataset.name;
          const dataName2 = activeBlock?.querySelector('.text-value-2')?.dataset.name;
          if (dataName1) {
            const h = document.createElement('input');
            h.type = 'hidden'; h.name = dataName1; h.value = text1Input?.value || '';
            form.appendChild(h);
          }
          if (dataName2) {
            const h = document.createElement('input');
            h.type = 'hidden'; h.name = dataName2; h.value = text2Input?.value || '';
            form.appendChild(h);
          }
          if (twoTextSubmitBs.independentfonts) {
            const f1Input = activeBlock?.querySelector('.fonts-holder-1 input:checked');
            const f1DataName = activeBlock?.querySelector('.font-value-1')?.dataset.name;
            const f2Input = activeBlock?.querySelector('.fonts-holder-2 input:checked');
            const f2DataName = activeBlock?.querySelector('.font-value-2')?.dataset.name;
            if (f1DataName) {
              const h = document.createElement('input');
              h.type = 'hidden'; h.name = f1DataName;
              h.value = f1Input?.value || activeBlock?.querySelector('.fonts-holder-1 input')?.value || '';
              form.appendChild(h);
            }
            if (f2DataName) {
              const h = document.createElement('input');
              h.type = 'hidden'; h.name = f2DataName;
              h.value = f2Input?.value || activeBlock?.querySelector('.fonts-holder-2 input')?.value || '';
              form.appendChild(h);
            }
          } else {
            const fontInput = activeBlock?.querySelector('.fonts-holder input:checked');
            const fontDataName = activeBlock?.querySelector('.font-value')?.dataset.name;
            if (fontDataName) {
              const h = document.createElement('input');
              h.type = 'hidden'; h.name = fontDataName;
              h.value = fontInput?.value || activeBlock?.querySelector('.fonts-holder input')?.value || '';
              form.appendChild(h);
            }
          }

        } else if (blockType === 'configurator-drawing-option') {
          const drawingValueInput = activeBlock?.querySelector('.drawing-upload-input');
          if (drawingValueInput) {
            const dataName = drawingValueInput.dataset.name;
            if (dataName && drawingValueInput.files && drawingValueInput.files.length > 0) {
              fileFields.push({ name: dataName, file: drawingValueInput.files[0] });
            }
          }
        } else if (blockType === 'configurator-image-option') {
          const imageValueInput = activeBlock?.querySelector('.image-upload-input');
          if (imageValueInput) {
            const dataName = imageValueInput.dataset.name;
            if (dataName && imageValueInput.files && imageValueInput.files.length > 0) {
              fileFields.push({ name: dataName, file: imageValueInput.files[0] });
            }
          }
        }

        // Collect SVG file if present
        const svgInput = activeBlock?.querySelector('.svg-upload-input');
        //console.log('[submit-svg] blockType:', blockType, 'svgInput:', !!svgInput, 'files:', svgInput?.files?.length ?? 'n/a');
        if (svgInput?.files?.length > 0 && svgInput.dataset.name) {
          fileFields.push({ name: svgInput.dataset.name, file: svgInput.files[0] });
        }

      });

      // Get the variant ID.
      var variantId = this.querySelector("#configurator_variant_select").value;
      const variantInput = document.createElement("input");
      variantInput.type = "hidden";
      variantInput.name = "id";
      variantInput.value = variantId;
      form.appendChild(variantInput);

      //console.log("Collected Form:", form);

      // Create a FormData object from the form.
      const formData = new FormData(form);

      // Append any file fields that were collected.
      fileFields.forEach((field) => {
        formData.append(field.name, field.file);
      });

      // ----- Loop through all canvas.final-preview-position and append the files -----
      const canvases = this.querySelectorAll(
        "configurator-preview canvas"
      );
			
      const canvasPromises = Array.from(canvases).map((canvas) => {
        return new Promise((resolve) => {
          // Ensure the canvas has rendered completely.
          requestAnimationFrame(() => {
            // Skip canvas if it hasn't been drawn yet (default size = 300×150)
            if (canvas.width <= 300 && canvas.height <= 150) {
              resolve();
              return;
            }

            canvas.toBlob((blob) => {
              const inputName = canvas.getAttribute("data-input-name");

              if (blob && inputName) {
                // Append the blob with a generated filename.
                formData.append(inputName, blob, `${inputName}.png`);
              }
              resolve();
            }, "image/png");
          });
        });
      });

      // After processing all canvas elements, submit the form.
      Promise.all(canvasPromises).then(() => {
        // Log only the key/value pairs.
        for (const [name, value] of formData.entries()) {
          //console.log(`${name}: ${value}`);
        }

        // Append extra parameters so that the server returns the updated cart sections
        const cartDrawerElement = document.querySelector("cart-drawer");
        if (cartDrawerElement) {
          // Retrieve the section IDs from the cart drawer component
          const sections = cartDrawerElement
            .getSectionsToRender()
            .map((section) => section.id);
          formData.append("sections", sections);
          formData.append("sections_url", window.location.pathname);
        }

        fetch("/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            //console.log("Product added to cart:", data);

            // Refresh and open the cart drawer if it exists and supports the renderContents method
            const cartDrawer = document.querySelector("cart-drawer");
            if (cartDrawer && typeof cartDrawer.renderContents === "function") {
              cartDrawer.renderContents(data);
            } else {
              console.error(
                "Cart drawer not found or renderContents method is not available."
              );
            }
            this.classList.remove("active");   
            this.querySelector(".product-form__submit").classList.remove("disabled");
            this.querySelector(".product-form__submit").classList.remove("loading");
            this.querySelector(".product-form__submit .loading-overlay__spinner").classList.add("hidden");

          })
          .catch((error) => {
            console.error("Error adding product to cart:", error);
          })
          .finally(() => {
            if (cartDrawerElement && cartDrawerElement.classList.contains('is-empty'))
              cartDrawerElement.classList.remove('is-empty');
          });
      });
    } else {
      this.querySelector("#configurator-consent-label").classList.add("error");
    }
  }

  getBlockSettings(activeBlock) {
    try {
      return activeBlock?.dataset.settings
        ? JSON.parse(activeBlock.dataset.settings)
        : {};
    } catch { return {}; }
  }

  updateMonogram(canvas = false) {
    //console.log("updateMonogram triggered");
    // Determine selector string based on canvas data-name attribute.
    let positionDetail = ".active"; // default: use the "active" class
    if (canvas && canvas.dataset.name) {
      positionDetail = '[data-name="' + canvas.dataset.name + '"]';
    }

    /*console.log(
      "looking for previewPosition:",
      "monogram-position-details" + positionDetail
    );*/

    // Use the selector string to get the preview position element.
    const previewPosition = this.querySelector(
      "monogram-position-details" + positionDetail
    );


    if (previewPosition) {
      /*console.log(
        "previewPosition found:",
        "monogram-position-details" + positionDetail
      );*/
      const positionHandle = previewPosition.dataset.name;
      const positionTitle = previewPosition.dataset.title;

      const colourElement =
        this.querySelector(
          '.product-form--radios[data-option="colour"] input[data-name="colour"]:checked'
        ) ||
        this.querySelector(
          '.product-form--radios[data-option="colour"] input[data-name="colour"]'
        );

      let previewBody = "";
      const colour = colourElement ? colourElement.dataset.value : this.dataset.currentColor;

      // Find the holder for the data.
      // If a canvas is passed (slider mode), get the parent `li` with the data.
      // If no canvas is passed (flip mode), use the already-found `previewPosition` element which now has the data.
      const dataHolder = canvas ? canvas.closest('[data-background-images]') : previewPosition;

      // Check metaobject images first
      if (dataHolder && dataHolder.dataset.backgroundImages) {
        try {
          const images = JSON.parse(dataHolder.dataset.backgroundImages);
          const holderColour = dataHolder.dataset.colour || colour;
          previewBody = images[holderColour] || Object.values(images).find(url => url) || '';

          if (previewBody) {
            //console.log(`[DEBUG] Found image for color '${holderColour}'`);
          } else {
            console.warn(`[DEBUG] No image found for color '${holderColour}'`);
          }
        } catch (e) {
          console.error('Failed to parse data-background-images from dataHolder:', e);
        }
      }
      // Fallback to upload
      if (!previewBody) {
        const uploadInput = this.querySelector("#previewImageUpload");
        if (uploadInput && uploadInput.files && uploadInput.files[0]) {
          previewBody = URL.createObjectURL(uploadInput.files[0]);
        } else {
          console.warn('Could not find a data holder with [data-background-images] and no upload available.');
        }
      }


			//NEW Flipped needs to be selected
			if (previewPosition.classList.contains('active')){
				var index = previewPosition.dataset.index
				var canvas_holder = this.querySelector('.canvas-holder')
				if(canvas_holder){

					if(index == "1"){
						canvas_holder.classList.remove('flipped')
					}else{
						canvas_holder.classList.add('flipped')
					}
				}

			}
     
      if (previewBody) {
        // Reassign canvas if it was not passed in.
        if (!canvas) {
          canvas = this.querySelector("configurator-preview canvas");
        }
        if (!canvas || typeof canvas.getContext !== "function") {
          console.error("Canvas element not found or invalid");
          return;
        }

        const ctx = canvas.getContext("2d");
        const img = new Image();
        img.src = previewBody;

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          //console.log("[DEBUG updateMonogram] Canvas dimensions: width=", canvas.width, "height=", canvas.height);

          // ─── Draw rotated, rounded rectangle from data-position ───
          const positionJSON = previewPosition.dataset.previewBox;
          if (positionJSON) {
            // Parse the JSON string from the data-preview-box attribute
            const positionParts = JSON.parse(positionJSON);
            //console.log("[DEBUG updateMonogram] Parsed positionParts:", positionParts);

            // Function to parse percentage or pixel values
            const parseDimension = (value, total) => {
              if (typeof value === 'string' && value.endsWith('%')) {
                return (parseFloat(value) / 100) * total;
              }
              return parseFloat(value) || 0; // Fallback to 0 if parsing fails
            };

            const rectX = parseDimension(positionParts.left, canvas.width);
            const rectY = parseDimension(positionParts.top, canvas.height);
            const rectWidth = parseDimension(positionParts.width, canvas.width);
            const rectHeight = parseDimension(positionParts.height, canvas.height);
            const rotateAngle = (parseFloat(positionParts.rotate) || 0) * (Math.PI / 180);

            // Compute border‐radius in pixels (from percent)
            const rawBorderPct = parseDimension(positionParts.borderradius, 100); // borderradius is itself a percentage, but of the rect dimensions
            // Horizontal radius = pct * rectWidth, vertical = pct * rectHeight
            let radiusX = (rawBorderPct / 100) * rectWidth;
            let radiusY = (rawBorderPct / 100) * rectHeight;

            // Clamp so we never exceed half of width/height
            radiusX = Math.min(radiusX, rectWidth / 2);
            radiusY = Math.min(radiusY, rectHeight / 2);

            // Log the aspect‐ratio and border radii
            const rawRatio = rectHeight / rectWidth;
            const rectRatio = parseFloat(rawRatio.toFixed(2));
            //console.log("rectRatio: ", rectRatio);

             const evt = new CustomEvent('ratioChanged', {
              detail: { rectRatio: rectRatio }
              });
    document.dispatchEvent(evt);

            if(!previewPosition.dataset.drawingRatio) {
              previewPosition.dataset.drawingRatio = rectRatio;
              const drawingPad = this.querySelector("drawing-pad");

							if(drawingPad) {
// 2) Tell it to switch to, say, 3× ratio right now:
drawingPad.setDrawingRatio(rectRatio);
							}
            }
            
            /*console.log(
              "radiusX(px):",
              Math.round(radiusX),
              "radiusY(px):",
              Math.round(radiusY)
            );*/

            // Helper: stroke the engraving rect with a given colour.
            // Defined here (in scope of rectX/Y, radiusX/Y, etc.) and called
            // after text drawing so the stroke can reflect overflow state.
            const drawRectBorder = (color) => {
              ctx.save();
              ctx.translate(rectX, rectY);
              ctx.rotate(rotateAngle);
              ctx.beginPath();
              if (radiusX >= rectWidth / 2 && radiusY >= rectHeight / 2) {
                ctx.ellipse(rectWidth / 2, rectHeight / 2, rectWidth / 2, rectHeight / 2, 0, 0, Math.PI * 2);
              } else if (radiusX > 0 || radiusY > 0) {
                ctx.moveTo(radiusX, 0);
                ctx.lineTo(rectWidth - radiusX, 0);
                ctx.quadraticCurveTo(rectWidth, 0, rectWidth, radiusY);
                ctx.lineTo(rectWidth, rectHeight - radiusY);
                ctx.quadraticCurveTo(rectWidth, rectHeight, rectWidth - radiusX, rectHeight);
                ctx.lineTo(radiusX, rectHeight);
                ctx.quadraticCurveTo(0, rectHeight, 0, rectHeight - radiusY);
                ctx.lineTo(0, radiusY);
                ctx.quadraticCurveTo(0, 0, radiusX, 0);
                ctx.closePath();
              } else {
                ctx.rect(0, 0, rectWidth, rectHeight);
              }
              ctx.strokeStyle = color;
              ctx.lineWidth = 1;
              ctx.stroke();
              ctx.restore();
            };

            // ─── Now continue with the existing logic (text/icon/drawing/image) ───
            const monogramSelect = previewPosition.querySelector(".monogram-option-select");
            if (monogramSelect && monogramSelect.options[monogramSelect.selectedIndex]?.disabled) {
              //console.log("No monogram option selected for this position yet. Only drawing background.");
              return; 
            }

            if(this.querySelector("monogram-position-details" + positionDetail +" .monogram-option-select")){
               var optionSelect = previewPosition.querySelector(".monogram-option-select").value.replace(/_/g, '-');
            }
              else {
               var optionSelect = 'setup';
              }
            //console.log("optionSelect element is monogram-position-details" + positionDetail + " .monogram-option-select with value ",optionSelect);

 if (optionSelect === 'setup') {

              const setupTypeSelect = previewPosition.querySelector('#SetupOptionType');
              const setupType = setupTypeSelect?.value || previewPosition.dataset.setupType || '';
              let typeSettings = {};
              try {
                if (previewPosition.dataset.typeSettings) {
                  typeSettings = JSON.parse(previewPosition.dataset.typeSettings);
                }
              } catch(e) { typeSettings = {}; }

              const effectiveParts = { ...positionParts, ...typeSettings };

              const isIcon     = setupType === 'configurator_icon_option';
              const isIconText = setupType === 'configurator_icon_text_option';
              const isTwoIcons = setupType === 'configurator_two_icons_option';
              const isTwoText  = setupType === 'configurator_two_text_option';

              if (isTwoText) {
                const fontInput = previewPosition.querySelector('.fonts-holder input:checked');
                const fontFamily = fontInput?.value || previewPosition.querySelector('.fonts-holder input')?.value;
                const text1Input = previewPosition.querySelector('#SetupTestText1');
                const text2Input = previewPosition.querySelector('#SetupTestText2');
                const rawText1 = text1Input?.value || '';
                const rawText2 = text2Input?.value || '';
                const text1 = effectiveParts.forceuppercase ? rawText1.toUpperCase() : rawText1;
                const text2 = effectiveParts.forceuppercase ? rawText2.toUpperCase() : rawText2;
                const min1 = effectiveParts.minfontsize1 || 1;
                const max1 = effectiveParts.maxfontsize1 || 10;
                const fs1 = Math.min(Math.max(effectiveParts.defaultfontsize1 || max1, min1), max1);
                const min2 = effectiveParts.minfontsize2 || 1;
                const max2 = effectiveParts.maxfontsize2 || 10;
                const fs2 = Math.min(Math.max(effectiveParts.defaultfontsize2 || max2, min2), max2);
                if (text1 || text2) {
                  const textParts = [
                    { text: text1, fontFamily, fontSize: fs1, length: 30 },
                    { text: text2, fontFamily, fontSize: fs2, length: 30 },
                  ];
                  this.appendMixedText(ctx, previewPosition, rectX, rectY, rectWidth, rectHeight, rotateAngle,
                    textParts, true, Math.max(fs1, fs2), effectiveParts);
                }

              } else if (isIcon || isTwoIcons) {
                const iconFontFamily = this.dataset.iconFontName || '';
                const iconTestInput = previewPosition.querySelector('#SetupTestIcon');
                const icon = iconTestInput?.value || '';
                const iconMinFont = effectiveParts.minfontsize || 1;
                const iconMaxFont = effectiveParts.maxfontsize || 10;
                const iconSize = Math.min(Math.max(effectiveParts.defaultfontsize || iconMaxFont, iconMinFont), iconMaxFont);
                if (icon) {
                  const text = isTwoIcons
                    ? (!effectiveParts.iconletteroneline ? icon + '\n' + icon : icon + icon)
                    : icon;
                  this.appendText(ctx, previewPosition, rectX, rectY, rectWidth, rectHeight, rotateAngle,
                    iconFontFamily, iconSize, text, effectiveParts);
                }

              } else if (isIconText) {
                const fontInput = previewPosition.querySelector('.fonts-holder input:checked');
                const letterFont = fontInput?.value || previewPosition.querySelector('.fonts-holder input')?.value;
                const textInput = previewPosition.querySelector('#SetupTestText');
                const rawLetter = textInput?.value || '';
                const letter = effectiveParts.forceuppercase ? rawLetter.toUpperCase() : rawLetter;
                const iconTestInput = previewPosition.querySelector('#SetupTestIcon');
                const icon = iconTestInput?.value || '';
                const iconTextMinFont = effectiveParts.minfontsize || 1;
                const iconTextMaxFont = effectiveParts.maxfontsize || 10;
                const fontSize = Math.min(Math.max(effectiveParts.defaultfontsize || iconTextMaxFont, iconTextMinFont), iconTextMaxFont);
                const newLine = !effectiveParts.iconletteroneline;
                if (letter || icon) {
                  const iconFirst = !!effectiveParts.flipicontext;
                  const iconFontFamilyForParts = this.dataset.iconFontName || letterFont;
                  const textParts = iconFirst
                    ? [
                        { text: icon,   fontFamily: iconFontFamilyForParts, fontSize: fontSize, length: 30 },
                        { text: letter, fontFamily: letterFont, fontSize: fontSize, length: 30 },
                      ]
                    : [
                        { text: letter, fontFamily: letterFont, fontSize: fontSize, length: 30 },
                        { text: icon,   fontFamily: iconFontFamilyForParts, fontSize: fontSize, length: 30 },
                      ];
                  this.appendMixedText(ctx, previewPosition, rectX, rectY, rectWidth, rectHeight, rotateAngle,
                    textParts, newLine, effectiveParts.maxfontsize || 12, effectiveParts);
                }

              } else {
                // Text type (or no type selected)
                const fontInput = previewPosition.querySelector('.fonts-holder input:checked');
                const fontFamily = fontInput?.value || previewPosition.querySelector('.fonts-holder input')?.value;
                const textInput = previewPosition.querySelector('#SetupTestText') || previewPosition.querySelector('#TestText');
                const rawText = textInput?.value || '';
                const text = effectiveParts.forceuppercase ? rawText.toUpperCase() : rawText;
                const curvedText = effectiveParts.curvedtext || false;
                const curvedTextStart = effectiveParts.curvedtextstart || 0;
                const setupMinFont = effectiveParts.minfontsize || 1;
                const setupMaxFont = effectiveParts.maxfontsize || 12;
                const setupFontSize = Math.min(Math.max(effectiveParts.defaultfontsize || setupMaxFont, setupMinFont), setupMaxFont);


                this.appendText(ctx, previewPosition, rectX, rectY, rectWidth, rectHeight, rotateAngle,
                  fontFamily, setupFontSize, text, effectiveParts,
                  (curvedText === true || curvedText === 'true')
                    ? { curvedText: true, curvedTextStart: curvedTextStart }
                    : false
                );
              }

              drawRectBorder('red');
	 return
            }
            // Reset overflow flag before app-mode drawing.
            canvas.dataset.overflow = '0';
            const activeBlock = previewPosition.querySelector('.monogram-block--' + optionSelect);

            if (!activeBlock) {
              console.error("Active monogram block not found for option:", optionSelect);
              return;
            }

            const fontSizeInput = activeBlock.querySelector(".font-size-input");
            const fontSize = fontSizeInput ? parseInt(fontSizeInput.value, 10) : 10;


            const iconSizeInput = activeBlock.querySelector(".icon-size-input");
            var iconSize = iconSizeInput ? parseInt(iconSizeInput.value, 10) : 10;

            const blockType = activeBlock.dataset.optionType || optionSelect;

            // SVG export: fixed reference dimensions (independent of canvas pixel size)
            const SVG_REF = 1000;
            const _svgW = typeof positionParts.width === 'string' && positionParts.width.endsWith('%')
              ? (parseFloat(positionParts.width) / 100) * SVG_REF
              : parseFloat(positionParts.width) || rectWidth;
            const _svgH = typeof positionParts.height === 'string' && positionParts.height.endsWith('%')
              ? (parseFloat(positionParts.height) / 100) * SVG_REF
              : parseFloat(positionParts.height) || rectHeight;
            const _svgRx = Math.min((rawBorderPct / 100) * _svgW, _svgW / 2);
            const _svgRy = Math.min((rawBorderPct / 100) * _svgH, _svgH / 2);

            if (blockType === "configurator-text-option") {
              const fontInput = activeBlock.querySelector(".fonts-holder input:checked");
              const fontFamily = fontInput ? fontInput.value : activeBlock.querySelector(".fonts-holder input")?.value;

              const textInput = activeBlock.querySelector(".field__input");
            const bs = this.getBlockSettings(activeBlock);
              const text = textInput ? (bs.forceuppercase ? textInput.value.toUpperCase() : textInput.value) : '';
            const curvedText      = bs.curvedtext      ?? positionParts.curvedtext      ?? false;
            const curvedTextStart = bs.curvedtextstart ?? positionParts.curvedtextstart ?? 0;
	            const mergedParts     = { ...positionParts, ...bs };



                const maxFontSize = Math.min(bs.maxfontsize ?? positionParts.maxfontsize ?? fontSize, fontSize);

              const finalFontPx = this.appendText(
                ctx,
                previewPosition,
                rectX,
                rectY,
                rectWidth,
                rectHeight,
                rotateAngle,
                fontFamily,
                maxFontSize,
                text,
                mergedParts,
                curvedText == true || curvedText === 'true'
                  ? { curvedText: true, curvedTextStart: curvedTextStart }
                  : false
              );

              // If auto-scaling shrunk the font, update the input to match.
              if (finalFontPx !== undefined && fontSizeInput) {
                const shrunkPct = Math.round((finalFontPx / canvas.width) * 100);
                if (shrunkPct < parseInt(fontSizeInput.value, 10)) {
                  fontSizeInput.value = shrunkPct;
                }
              }

              // Fire-and-forget SVG generation (straight and curved text)
              {
                const svgInput = activeBlock?.querySelector('.svg-upload-input');
                if (svgInput && fontFamily && text) {
                  const fontUrl = activeBlock.querySelector(`.fonts-holder [data-font-url][data-font="${fontFamily}"]`)?.dataset.fontUrl
                               || activeBlock.querySelector('.fonts-holder [data-font-url]')?.dataset.fontUrl;
                  if (fontUrl) {
                    const isCurved = curvedText == true || curvedText === 'true';
                    // Font size must scale against SVG_REF (full canvas width), not _svgW (engraving area),
                    // to match the canvas which uses (fontSize/100) * canvas.width.
                    const _svgFontSizePx = (maxFontSize / 100) * SVG_REF;
                    const _text = text;
                    const _startDeg = curvedTextStart;
                    fontLoader.load(fontFamily, fontUrl).then(font => {
                      if (!font) return;
                      const svgStr = isCurved
                        ? generateCurvedTextSVG(font, _text, _svgW, _svgH, _svgFontSizePx, _svgRx, _svgRy, _startDeg)
                        : generateTextSVG(font, _text, _svgW, _svgH, _svgFontSizePx, _svgRx, _svgRy);
                      const dt = new DataTransfer();
                      dt.items.add(new File([svgStr], 'engraving.svg', { type: 'image/svg+xml' }));
                      svgInput.files = dt.files;
                    });
                  }
                }
              }

            } else if (blockType === 'configurator-icon-option') {
              const iconFontName = activeBlock.dataset.font;

              const iconInput = activeBlock.querySelector('.font-icons-holder input:checked');
              const icon = iconInput ? iconInput.value : '';

              if (icon) {
                this.appendText(
                  ctx,
                  previewPosition,
                  rectX,
                  rectY,
                  rectWidth,
                  rectHeight,
                  rotateAngle,
                  iconFontName,
                  iconSize,
                  icon,
                  positionParts
                );

                // Fire-and-forget SVG generation for icon
                {
                  const svgInput = activeBlock?.querySelector('.svg-upload-input');
                  const holder = activeBlock.querySelector('.font-icons-holder');
                  const resolvedFontUrl  = holder?.dataset.fontUrl  || this.dataset.iconFontUrl;
                  const resolvedFontName = iconFontName             || this.dataset.iconFontName;
                  if (svgInput) {
                    if (resolvedFontUrl && resolvedFontName) {
                      const _svgFontSizePx = (iconSize / 100) * SVG_REF;
                      fontLoader.load(resolvedFontName, resolvedFontUrl).then(font => {
                        if (!font) return;
                        const svgStr = generateTextSVG(font, icon, _svgW, _svgH, _svgFontSizePx, _svgRx, _svgRy);
                        const dt = new DataTransfer();
                        dt.items.add(new File([svgStr], 'engraving.svg', { type: 'image/svg+xml' }));
                        svgInput.files = dt.files;
                      });
                    } else {
                      console.warn('[icon-svg] skipped — missing fontUrl or fontName');
                    }
                  }
                }
              }
            } else if (blockType === 'configurator-two-icons-option') {
              const iconFontName = activeBlock.dataset.font;

              const firstIconInput = activeBlock.querySelector('.font-icons-holder.font-first-icons-holder input:checked');
              const firstIcon = firstIconInput ? firstIconInput.value : '';

              const secondIconInput = activeBlock.querySelector('.font-icons-holder.font-second-icons-holder input:checked');
              const secondIcon = secondIconInput ? secondIconInput.value : '';
              
              let newLine = activeBlock.dataset.newLine == '1';

              if (firstIcon || secondIcon) {
                const iconParts = [];
                if (firstIcon) iconParts.push(firstIcon);
                if (secondIcon) iconParts.push(secondIcon);
                const textString = iconParts.join(newLine ? '\n' : '');

                this.appendText(
                  ctx,
                  previewPosition,
                  rectX,
                  rectY,
                  rectWidth,
                  rectHeight,
                  rotateAngle,
                  iconFontName,
                  iconSize,
                  textString,
                  positionParts
                );

                // Fire-and-forget SVG generation for two icons
                {
                  const svgInput = activeBlock?.querySelector('.svg-upload-input');
                  if (svgInput && firstIcon && secondIcon) {
                    const holder = activeBlock.querySelector('.font-icons-holder');
                    const resolvedFontUrl  = holder?.dataset.fontUrl  || this.dataset.iconFontUrl;
                    const resolvedFontName = iconFontName             || this.dataset.iconFontName;
                    if (resolvedFontUrl && resolvedFontName) {
                      const _svgFontSizePx = (iconSize / 100) * SVG_REF;
                      const combinedText = iconParts.join(newLine ? '\n' : '');
                      fontLoader.load(resolvedFontName, resolvedFontUrl).then(font => {
                        if (!font) return;
                        const svgStr = generateTextSVG(font, combinedText, _svgW, _svgH, _svgFontSizePx, _svgRx, _svgRy);
                        const dt = new DataTransfer();
                        dt.items.add(new File([svgStr], 'engraving.svg', { type: 'image/svg+xml' }));
                        svgInput.files = dt.files;
                      });
                    }
                  }
                }
              }
            } else if (
              blockType === 'conigurator-icon-text-option' ||
              blockType === 'configurator-icon-text-option'
            ) {

              const letterFontInput = activeBlock.querySelector(".fonts-holder input:checked");
              const letterFont = letterFontInput ? letterFontInput.value : activeBlock.querySelector(".fonts-holder input")?.value;

              const orientationInput = activeBlock.querySelector(".orientation-holder input:checked");
              const orientation = orientationInput ? orientationInput.value : '';

              const letterInput = activeBlock.querySelector(".product-form__input .field .field__input");
              const iconTextBs = this.getBlockSettings(activeBlock);
              const letter = letterInput ? (iconTextBs.forceuppercase ? letterInput.value.toUpperCase() : letterInput.value) : '';

              const iconFont = activeBlock.dataset.font;

              const iconInput = activeBlock.querySelector(".icons-holder input:checked");
              const icon = iconInput ? iconInput.value : '';

              let newLine = activeBlock.dataset.newLine == '1';

              if ((letter || icon) && letterFont && iconFont) {
                let textParts = [];
                const iconFirst = iconTextBs.flipicontext ? (orientation === 'text-icon') : (orientation !== 'text-icon');
                if (iconFirst) {
                   textParts = [
                      { text: icon, fontFamily: iconFont, fontSize: iconSize, length: 30 },
                      { text: letter, fontFamily: letterFont, fontSize: fontSize, length: 30 },
                    ];
                } else {
                  textParts = [
                      { text: letter, fontFamily: letterFont, fontSize: fontSize, length: 30 },
                      { text: icon, fontFamily: iconFont, fontSize: iconSize, length: 30 },
                    ];
                }
                const iconTextScale = this.appendMixedText(
                    ctx,
                    previewPosition,
                    rectX,
                    rectY,
                    rectWidth,
                    rectHeight,
                    rotateAngle,
                    textParts,
                    newLine,
                    12,
                    positionParts
                  );

                // If auto-scaling shrunk the text, update the font size input to match.
                if (iconTextScale !== undefined && iconTextScale < 1.0 && fontSizeInput) {
                  const shrunkPct = Math.round(fontSize * iconTextScale);
                  if (shrunkPct < parseInt(fontSizeInput.value, 10)) {
                    fontSizeInput.value = shrunkPct;
                  }
                }

                // Fire-and-forget SVG generation for icon-text
                {
                  const svgInput = activeBlock?.querySelector('.svg-upload-input');
                  if (svgInput) {
                  const iconFontHolder = activeBlock.querySelector('.orientation-holder');
                  const iconFontUrl = iconFontHolder?.dataset.fontUrl || this.dataset.iconFontUrl;
                  const letterFontUrl = activeBlock.querySelector(`.fonts-holder [data-font-url][data-font="${letterFont}"]`)?.dataset.fontUrl
                                     || activeBlock.querySelector('.fonts-holder [data-font-url]')?.dataset.fontUrl;
                  if (iconFontUrl && letterFontUrl) {
                    const _svgIconSizePx   = (iconSize / 100) * SVG_REF;
                    const _svgLetterSizePx = (fontSize / 100) * SVG_REF;
                    const segsOrdered = iconFirst
                      ? [{ fontName: iconFont,    fontUrl: iconFontUrl,    text: icon,   fontSizePx: _svgIconSizePx },
                         { fontName: letterFont,  fontUrl: letterFontUrl,  text: letter, fontSizePx: _svgLetterSizePx }]
                      : [{ fontName: letterFont,  fontUrl: letterFontUrl,  text: letter, fontSizePx: _svgLetterSizePx },
                         { fontName: iconFont,    fontUrl: iconFontUrl,    text: icon,   fontSizePx: _svgIconSizePx }];
                    Promise.all(segsOrdered.map(s => fontLoader.load(s.fontName, s.fontUrl))).then(fonts => {
                      if (fonts.some(f => !f)) return;
                      const segments = segsOrdered.map((s, i) => ({ font: fonts[i], text: s.text, fontSizePx: s.fontSizePx }));
                      const svgStr = generateMixedTextSVG(segments, _svgW, _svgH, _svgRx, _svgRy, 12);
                      const dt = new DataTransfer();
                      dt.items.add(new File([svgStr], 'engraving.svg', { type: 'image/svg+xml' }));
                      svgInput.files = dt.files;
                    });
                  }
                }
              }
            }
            } else if (blockType === 'configurator-two-text-option') {
              const twoTextBs = this.getBlockSettings(activeBlock);
              const text1Input = activeBlock.querySelector('.field__input--text-1');
              const text2Input = activeBlock.querySelector('.field__input--text-2');
              const fs1Input = activeBlock.querySelector('.font-size-input-1');
              const fs2Input = activeBlock.querySelector('.font-size-input-2');
              const text1 = text1Input ? (twoTextBs.forceuppercase ? text1Input.value.toUpperCase() : text1Input.value) : '';
              const text2 = text2Input ? (twoTextBs.forceuppercase ? text2Input.value.toUpperCase() : text2Input.value) : '';
              const fontSize1 = fs1Input ? parseInt(fs1Input.value, 10) : 10;
              const fontSize2 = fs2Input ? parseInt(fs2Input.value, 10) : 10;
              let fontFamily1, fontFamily2;
              if (twoTextBs.independentfonts) {
                const f1Input = activeBlock.querySelector('.fonts-holder-1 input:checked');
                fontFamily1 = f1Input?.value || activeBlock.querySelector('.fonts-holder-1 input')?.value;
                const f2Input = activeBlock.querySelector('.fonts-holder-2 input:checked');
                fontFamily2 = f2Input?.value || activeBlock.querySelector('.fonts-holder-2 input')?.value;
              } else {
                const fInput = activeBlock.querySelector('.fonts-holder input:checked');
                fontFamily1 = fontFamily2 = fInput?.value || activeBlock.querySelector('.fonts-holder input')?.value;
              }
              if (text1 || text2) {
                const textParts = [
                  { text: text1, fontFamily: fontFamily1, fontSize: fontSize1, length: 30 },
                  { text: text2, fontFamily: fontFamily2, fontSize: fontSize2, length: 30 },
                ];
                this.appendMixedText(ctx, previewPosition, rectX, rectY, rectWidth, rectHeight, rotateAngle,
                  textParts, true, Math.max(fontSize1, fontSize2), positionParts);
              }

              // Fire-and-forget SVG generation for two text
              {
                const svgInput = activeBlock?.querySelector('.svg-upload-input');
                if (svgInput && (text1 || text2)) {
                  const fontUrl1 = twoTextBs.independentfonts
                    ? (activeBlock.querySelector(`.fonts-holder-1 [data-font-url][data-font="${fontFamily1}"]`)?.dataset.fontUrl
                       || activeBlock.querySelector('.fonts-holder-1 [data-font-url]')?.dataset.fontUrl)
                    : (activeBlock.querySelector(`.fonts-holder [data-font-url][data-font="${fontFamily1}"]`)?.dataset.fontUrl
                       || activeBlock.querySelector('.fonts-holder [data-font-url]')?.dataset.fontUrl);
                  const fontUrl2 = twoTextBs.independentfonts
                    ? (activeBlock.querySelector(`.fonts-holder-2 [data-font-url][data-font="${fontFamily2}"]`)?.dataset.fontUrl
                       || activeBlock.querySelector('.fonts-holder-2 [data-font-url]')?.dataset.fontUrl)
                    : fontUrl1;
                  if (fontUrl1 && fontUrl2) {
                    const _svgFontSizePx1 = (fontSize1 / 100) * SVG_REF;
                    const _svgFontSizePx2 = (fontSize2 / 100) * SVG_REF;
                    const _lineSpacing = Math.max(fontSize1, fontSize2);
                    Promise.all([
                      fontLoader.load(fontFamily1, fontUrl1),
                      fontLoader.load(fontFamily2, fontUrl2)
                    ]).then(([font1, font2]) => {
                      if (!font1 || !font2) return;
                      const segments = [
                        { font: font1, text: text1, fontSizePx: _svgFontSizePx1 },
                        { font: font2, text: text2, fontSizePx: _svgFontSizePx2 }
                      ];
                      const svgStr = generateMixedTextSVG(segments, _svgW, _svgH, _svgRx, _svgRy, _lineSpacing);
                      const dt = new DataTransfer();
                      dt.items.add(new File([svgStr], 'engraving.svg', { type: 'image/svg+xml' }));
                      svgInput.files = dt.files;
                    });
                  }
                }
              }

            } else if (blockType === 'configurator-drawing-option') {
              const drawingPad = activeBlock.querySelector('drawing-pad');
              this.appendDrawing(
                ctx,
                previewPosition,
                rectX,
                rectY,
                rectWidth,
                rectHeight,
                rotateAngle,
                drawingPad
              );
            } else if (blockType === 'configurator-image-option') {
              const fileInput = activeBlock.querySelector('.image-upload-label input');
              this.appendImage(
                ctx,
                previewPosition,
                rectX,
                rectY,
                rectWidth,
                rectHeight,
                rotateAngle,
                fileInput,
                positionParts
              );
            }

            // In app mode: border is transparent until text overflows, then red.
            const isOverflow = canvas.dataset.overflow === '1';
            drawRectBorder(isOverflow ? 'red' : 'transparent');

            // Toggle modal class and at-limit flag on text inputs.
            const modal = this.closest('.product-configurator-modal');
            if (modal) modal.classList.toggle('text-at-limit', isOverflow);
            if (activeBlock) {
              activeBlock.querySelectorAll('input[type="text"].field__input, textarea.field__input')
                .forEach(el => {
                  if (isOverflow) {
                    el.dataset.atLimit = 'true';
                    el.setCustomValidity(
                      'Your text has reached the minimum font size for this engraving area. ' +
                      'Please remove some characters before adding more.'
                    );
                  } else {
                    delete el.dataset.atLimit;
                    el.setCustomValidity('');
                  }
                });
            }

          }
        };

      }
    }
    this.checkForFinalPreview();
    this.saveConfiguratorState();
  }

  // Return the human-readable summary value for a position.
  // Drawing / image: the option's display title from the select.
  // Text: the entered text.
  // Icon: the icon display name (data-title on the radio).
  // Icon + text: "text / Icon Name" (respecting orientation).
  // Two icons: "Icon One / Icon Two".
  // Two text: "Text One / Text Two".
  getPositionSummaryValue(posEl) {
    const select = posEl.querySelector('.monogram-option-select');
    const blockTitle = select?.value.replace(/_/g, '-') || '';
    const activeBlock = blockTitle ? posEl.querySelector('.monogram-block--' + blockTitle) : null;
    const blockType = activeBlock?.dataset.optionType || '';

    if (!blockType || blockType.includes('drawing') || blockType.includes('image')) {
      return select?.options[select.selectedIndex]?.text?.trim() || '';
    }

    if (blockType === 'configurator-text-option') {
      const bs = this.getBlockSettings(activeBlock);
      const raw = activeBlock.querySelector('.field__input')?.value || '';
      return bs.forceuppercase ? raw.toUpperCase() : raw;
    }

    if (blockType === 'configurator-icon-option') {
      return activeBlock.querySelector('.font-icons-holder input:checked')?.dataset.title || '';
    }

    if (blockType === 'configurator-two-icons-option') {
      const first  = activeBlock.querySelector('.font-first-icons-holder input:checked')?.dataset.title || '';
      const second = activeBlock.querySelector('.font-second-icons-holder input:checked')?.dataset.title || '';
      return [first, second].filter(Boolean).join(' / ');
    }

    if (blockType === 'configurator-icon-text-option') {
      const bs = this.getBlockSettings(activeBlock);
      const rawText = activeBlock.querySelector('.field__input')?.value || '';
      const text = bs.forceuppercase ? rawText.toUpperCase() : rawText;
      const iconTitle = activeBlock.querySelector('.icons-holder input:checked')?.dataset.title || '';
      const orientation = activeBlock.querySelector('.orientation-holder input:checked')?.value || '';
      const iconFirst = bs.flipicontext ? (orientation === 'text-icon') : (orientation !== 'text-icon');
      return [iconFirst ? iconTitle : text, iconFirst ? text : iconTitle].filter(Boolean).join(' / ');
    }

    if (blockType === 'configurator-two-text-option') {
      const bs = this.getBlockSettings(activeBlock);
      const raw1 = activeBlock.querySelector('.field__input--text-1')?.value || '';
      const raw2 = activeBlock.querySelector('.field__input--text-2')?.value || '';
      const t1 = bs.forceuppercase ? raw1.toUpperCase() : raw1;
      const t2 = bs.forceuppercase ? raw2.toUpperCase() : raw2;
      return [t1, t2].filter(Boolean).join(' / ');
    }

    return '';
  }

  appendDrawing(
    ctx,
    previewPosition,
    rectX,
    rectY,
    rectWidth,
    rectHeight,
    rotateAngle,
    drawingPad = false
  ) {
    // 1) find the active drawing-pad
    if (!drawingPad) {
      drawingPad = this.querySelector(
        "monogram-position-details.active drawing-pad"
      );
    }
    if (!drawingPad) {
      console.error("Drawing pad not found");
      return;
    }

    // 2) grab the SVG data-URL from SignaturePad
    const svgDataUrl = drawingPad.signaturePad?.toDataURL("image/svg+xml");
    if (!svgDataUrl) {
      console.warn("No SVG data-URL available");
      return;
    }

    // 3) split off header vs payload
    const [header, payload] = svgDataUrl.split(",");
    let svgMarkup;
    if (header.includes(";base64")) {
      // SignaturePad gave us a base64 payload
      svgMarkup = atob(payload);
    } else {
      // SignaturePad gave us a percent-encoded payload
      svgMarkup = decodeURIComponent(payload);
    }

    // 4) make a File from the raw SVG text
    const svgBlob = new Blob([svgMarkup], {
      type: "image/svg+xml;charset=utf-8",
    });
    const svgFile = new File([svgBlob], "drawing.svg", {
      type: "image/svg+xml",
    });

    // 5) shove it into your <input type="file" class="drawing-upload-input">
    const drawingUploadInput = drawingPad.querySelector(
      ".drawing-upload-input"
    );
    if (drawingUploadInput) {
      const dt = new DataTransfer();
      dt.items.add(svgFile);
      drawingUploadInput.files = dt.files;
      //console.log("✅ SVG file loaded into .drawing-upload-input");
    } else {
      console.warn("No .drawing-upload-input found");
    }

    // 6) (optional) preview a faint raster of the SVG on your ctx
    const img = new Image();
    img.src = svgDataUrl; // browser will render SVG natively
    img.onload = () => {
      ctx.save();
      ctx.translate(rectX, rectY);
      ctx.rotate(rotateAngle);
      ctx.globalAlpha = 0.2;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(
        img,
        0,
        0,
        rectWidth,
        rectHeight
      );
      ctx.restore();
    };
    img.onerror = (err) => {
      console.error("Error loading SVG for preview:", err);
    };
  }

appendText(
  ctx,
  previewPosition,
  rectX,
  rectY,
  rectWidth,
  rectHeight,
  rotateAngle,
  fontFamily,
  fontSize,
  text,
  parsedPositionParts, // New argument for the JSON object
  curvedText = false
) {
  // ─── 1) FALLBACKS ───────────────────────────────────────────────────────────
  if (!fontFamily) {
    fontFamily =
      this.querySelector(
        "monogram-position-details.active .fonts-holder input:checked"
      )?.value ||
      this.querySelector(
        "monogram-position-details.active .fonts-holder input"
      )?.value;
  }
  if (!fontSize) fontSize = 12;

  // If no text was passed in, grab from the textarea
  if (!text) {
    const ta = this.querySelector(
      "monogram-position-details.active .monogram-block--configurator-text-option textarea"
    );
    if (!ta || !ta.value) return;
    text = ta.value;
  }
  // ─── 2) EXTRACT BORDER RADIUS FROM DATA-POSITION ────────────────────────────
  // We need rx, ry (in px) to trace the rounded corners
  const rawBorderPct = parsedPositionParts?.borderradius ? parseFloat(parsedPositionParts.borderradius) : 0;
  let rx = (rawBorderPct / 100) * rectWidth;
  let ry = (rawBorderPct / 100) * rectHeight;
  // Clamp so corner radii do not exceed half of respective dimensions
  rx = Math.min(rx, rectWidth / 2);
  ry = Math.min(ry, rectHeight / 2);

  // ─── 3) SET FONT FOR MEASUREMENT ─────────────────────────────────────────────
  let fontSizePx = (fontSize / 100) * ctx.canvas.width;
  ctx.font = `${fontSizePx}px ${fontFamily}`;
  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(0,0,0,0.3)";

  // ─── 4) HANDLE CURVED TEXT AROUND ROUNDED RECTANGLE ─────────────────────────
  if (
    curvedText &&
    typeof curvedText === "object" &&
    curvedText.curvedText === true
  ) {
    // 4a) Compute the length of each straight segment
    const w = rectWidth,
      h = rectHeight;
    const straightTop = w - 2 * rx;
    const straightRight = h - 2 * ry;
    const straightBottom = w - 2 * rx;
    const straightLeft = h - 2 * ry;

    // 4b) Approximate quarter-ellipse corner length for each corner
    //     using Ramanujan’s ellipse circumference approximation:
    //       fullEllipseCirc = Math.PI * [3(a + b) − sqrt((3a + b)(a + 3b))],
    //     then cornerLength = fullEllipseCirc / 4
    const a = rx,
      b = ry;
    const fullEllipseCirc =
      Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
    const cornerLength = fullEllipseCirc / 4;

    // 4c) Total perimeter around the rounded rectangle
    const P =
      straightTop +
      cornerLength +
      straightRight +
      cornerLength +
      straightBottom +
      cornerLength +
      straightLeft +
      cornerLength;

    // 4d) Map start angle (degrees) to an offset along that perimeter
    const startDeg = (curvedText.curvedTextStart || 0) % 360;
    const startOffset = (startDeg / 360) * P;

    // 4e) Break text into chars and measure widths
    const chars = Array.from(text);
    const charWidths = chars.map((ch) => ctx.measureText(ch).width);

    // This is an approximation. It assumes the text is not long enough to wrap
    // from the top to the bottom half. It checks the starting angle to decide
    // the orientation for the entire block of text.
    const isBottomHalf = startDeg > 90 && startDeg < 270;

      ctx.textBaseline = "top";

		if(isBottomHalf){
			chars.reverse()
			charWidths.reverse()
		}

    // 4f) Build a cumulative array of distances from the text start (center of each glyph)
    // cumulative[0] = 0
    // cumulative[1] = width of char[0]
    // cumulative[2] = width of char[0] + width of char[1], etc.

		//additional spacing
		const letterSpacing = fontSizePx * 0.3

    const cumulative = [0];
    for (let i = 0; i < charWidths.length; i++) {
      cumulative.push(cumulative[i] + charWidths[i] + letterSpacing);
    }
		const totalTextWidth = cumulative[cumulative.length - 1]
		const centeredStartOffset = startOffset - totalTextWidth/2

    // 4g) For each character, compute and possibly stop when the loop is complete
    for (let i = 0; i < chars.length; i++) {
      const halfChar = charWidths[i] / 2;
      // distanceFromStart = cumulative[i] + halfChar
      const distanceFromStart = cumulative[i] + halfChar;
      // Stop drawing once distanceFromStart ≥ P
      if (distanceFromStart >= P) break;

      // Otherwise, compute rawOffset = startOffset + distanceFromStart
      // Then wrap to [0, P) with modulo
      //const rawOffset = startOffset + distanceFromStart;
      //const offsetAlong = rawOffset % P;
			//	NOW strt from centre 
      const rawOffset = centeredStartOffset + distanceFromStart;
			const offsetAlong = ((rawOffset % P) + P) % P; // safe modulo for negative values

      let localX = 0,
        localY = 0,
        tangentAngle = 0;

      // ─── Top straight ──────────────────────────────────────────────────────
      if (offsetAlong < straightTop) {
        localX = -w / 2 + rx + offsetAlong;
        localY = -h / 2;
        tangentAngle = 0;
      }
      // ─── Top-right corner ──────────────────────────────────────────────────
      else if (offsetAlong < straightTop + cornerLength) {
        const t = offsetAlong - straightTop;
        // Map t ∈ [0, cornerLength] → φ ∈ [−π/2, 0]
        const φ = -Math.PI / 2 + (t / cornerLength) * (Math.PI / 2);
        const cx = w / 2 - rx;
        const cy = -h / 2 + ry;
        localX = cx + rx * Math.cos(φ);
        localY = cy + ry * Math.sin(φ);
        tangentAngle = φ + Math.PI / 2;
      }
      // ─── Right straight ────────────────────────────────────────────────────
      else if (
        offsetAlong < straightTop + cornerLength + straightRight
      ) {
        const t = offsetAlong - (straightTop + cornerLength);
        localX = w / 2;
        localY = -h / 2 + ry + t;
        tangentAngle = Math.PI / 2;
      }
      // ─── Bottom-right corner ───────────────────────────────────────────────
      else if (
        offsetAlong <
        straightTop + cornerLength + straightRight + cornerLength
      ) {
        const t =
          offsetAlong - (straightTop + cornerLength + straightRight);
        // Map t ∈ [0, cornerLength] → φ ∈ [0, π/2]
        const φ = (t / cornerLength) * (Math.PI / 2);
        const cx = w / 2 - rx;
        const cy = h / 2 - ry;
        localX = cx + rx * Math.cos(φ);
        localY = cy + ry * Math.sin(φ);
        tangentAngle = φ + Math.PI / 2;
      }
      // ─── Bottom straight ───────────────────────────────────────────────────
      else if (
        offsetAlong <
        straightTop +
          cornerLength +
          straightRight +
          cornerLength +
          straightBottom
      ) {
        const t =
          offsetAlong -
          (straightTop + cornerLength + straightRight + cornerLength);
        localX = w / 2 - rx - t;
        localY = h / 2;
        tangentAngle = Math.PI;
      }
      // ─── Bottom-left corner ────────────────────────────────────────────────
      else if (
        offsetAlong <
        straightTop +
          cornerLength +
          straightRight +
          cornerLength +
          straightBottom +
          cornerLength
      ) {
        const t =
          offsetAlong -
          (straightTop +
            cornerLength +
            straightRight +
            cornerLength +
            straightBottom);
        // Map t ∈ [0, cornerLength] → φ ∈ [π/2, π]
        const φ =
          Math.PI / 2 + (t / cornerLength) * (Math.PI / 2);
        const cx = -w / 2 + rx;
        const cy = h / 2 - ry;
        localX = cx + rx * Math.cos(φ);
        localY = cy + ry * Math.sin(φ);
        tangentAngle = φ + Math.PI / 2;
      }
      // ─── Left straight ─────────────────────────────────────────────────────
      else if (
        offsetAlong <
        straightTop +
          cornerLength +
          straightRight +
          cornerLength +
          straightBottom +
          cornerLength +
          straightLeft
      ) {
        const t =
          offsetAlong -
          (straightTop +
            cornerLength +
            straightRight +
            cornerLength +
            straightBottom +
            cornerLength);
        localX = -w / 2;
        localY = h / 2 - ry - t;
        tangentAngle = (3 * Math.PI) / 2;
      }
      // ─── Top-left corner ────────────────────────────────────────────────────
      else {
        const t =
          offsetAlong -
          (straightTop +
            cornerLength +
            straightRight +
            cornerLength +
            straightBottom +
            cornerLength +
            straightLeft);
        // Map t ∈ [0, cornerLength] → φ ∈ [π, 3π/2]
        const φ =
          Math.PI + (t / cornerLength) * (Math.PI / 2);
        const cx = -w / 2 + rx;
        const cy = -h / 2 + ry;
        localX = cx + rx * Math.cos(φ);
        localY = cy + ry * Math.sin(φ);
        tangentAngle = φ + Math.PI / 2;
      }

      // 4h) APPLY RECTANGLE ROTATION to point and tangent (origin = top-left)
      const cosA = Math.cos(rotateAngle);
      const sinA = Math.sin(rotateAngle);
      const tlX = localX + rectWidth / 2;
      const tlY = localY + rectHeight / 2;
      const rotatedX = tlX * cosA - tlY * sinA;
      const rotatedY = tlX * sinA + tlY * cosA;
      const drawX = rectX + rotatedX;
      const drawY = rectY + rotatedY;
      const finalAngle = tangentAngle + rotateAngle;

      // 4i) RENDER CHARACTER at (drawX, drawY)
      ctx.save();
      ctx.translate(drawX, drawY);
			if (isBottomHalf) {
				ctx.rotate(finalAngle + Math.PI);
				ctx.textBaseline = "bottom";
			} else {
				ctx.rotate(finalAngle);
			}
      //ctx.rotate(finalAngle);
      ctx.fillText(chars[i], 0, 0);
      ctx.restore();
    }

    return; // done with curved text
  }

  // ─── 5) STRAIGHT (MULTI-LINE) TEXT CASE ────────────────────────────────────
  function wrapParagraph(paragraph, maxWidth) {
    const words = paragraph.split(" ");
    const lines = [];
    let line = "";
    for (let w of words) {
      const test = line ? line + " " + w : w;
      if (ctx.measureText(test).width <= maxWidth) {
        line = test;
      } else {
        if (line) lines.push(line);
        line = w;
      }
    }
    if (line) lines.push(line);
    return lines;
  }

  const paragraphs = text.split(/\r?\n/);
  let lines = [];
  for (let p of paragraphs) {
    lines.push(...wrapParagraph(p, rectWidth));
  }

  let lineHeight = fontSizePx * 1.2;
  let maxLineWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));

  const minFontSizePx = parsedPositionParts?.minfontsize
    ? (parsedPositionParts.minfontsize / 100) * ctx.canvas.width
    : 5;
  const initialFontSizePx = fontSizePx;
  while (
    (maxLineWidth > rectWidth || lines.length * lineHeight > rectHeight) &&
    fontSizePx > minFontSizePx
  ) {
    fontSizePx *= 0.95;
    ctx.font = `${fontSizePx}px ${fontFamily}`;
    lineHeight = fontSizePx * 1.2;
    lines = [];
    for (let p of paragraphs) {
      lines.push(...wrapParagraph(p, rectWidth));
    }
    maxLineWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
  }

  // Clamp to minimum (0.95 steps may overshoot).
  if (fontSizePx < minFontSizePx) {
    fontSizePx = minFontSizePx;
    ctx.font = `${fontSizePx}px ${fontFamily}`;
    lineHeight = fontSizePx * 1.2;
  }

  // Signal overflow only when at the minimum size AND text still doesn't fit.
  if (fontSizePx <= minFontSizePx && (maxLineWidth > rectWidth || lines.length * lineHeight > rectHeight)) {
    ctx.canvas.dataset.overflow = '1';
  }

  const totalH = lines.length * lineHeight;
  const startY = -totalH / 2 + lineHeight / 2;

  ctx.save();
  ctx.translate(rectX, rectY);
  ctx.rotate(rotateAngle);
  ctx.textBaseline = "middle";
  ctx.beginPath();
  if (rx >= rectWidth / 2 && ry >= rectHeight / 2) {
    ctx.ellipse(rectWidth / 2, rectHeight / 2, rectWidth / 2, rectHeight / 2, 0, 0, Math.PI * 2);
  } else if (rx > 0 || ry > 0) {
    ctx.moveTo(rx, 0);
    ctx.lineTo(rectWidth - rx, 0);
    ctx.quadraticCurveTo(rectWidth, 0, rectWidth, ry);
    ctx.lineTo(rectWidth, rectHeight - ry);
    ctx.quadraticCurveTo(rectWidth, rectHeight, rectWidth - rx, rectHeight);
    ctx.lineTo(rx, rectHeight);
    ctx.quadraticCurveTo(0, rectHeight, 0, rectHeight - ry);
    ctx.lineTo(0, ry);
    ctx.quadraticCurveTo(0, 0, rx, 0);
    ctx.closePath();
  } else {
    ctx.rect(0, 0, rectWidth, rectHeight);
  }
  ctx.clip();

  lines.forEach((lineText, i) => {
    ctx.fillText(lineText, rectWidth / 2, rectHeight / 2 + startY + i * lineHeight);
  });

  ctx.restore();
  return fontSizePx;
}


  // New method to handle multiple segments (mixed fonts) in one text block.
  appendMixedText(
    ctx,
    previewPosition,
    rectX,
    rectY,
    rectWidth,
    rectHeight,
    rotateAngle,
    segments,
    alwaysNewLine = true,
    lineSpacing = 12,
    parsedPositionParts = null
  ) {
    const rawBorderPct = parsedPositionParts?.borderradius ? parseFloat(parsedPositionParts.borderradius) : 0;
    let mrx = Math.min((rawBorderPct / 100) * rectWidth, rectWidth / 2);
    let mry = Math.min((rawBorderPct / 100) * rectHeight, rectHeight / 2);

    ctx.save();
    ctx.translate(rectX, rectY);
    ctx.rotate(rotateAngle);
    ctx.beginPath();
    if (mrx >= rectWidth / 2 && mry >= rectHeight / 2) {
      ctx.ellipse(rectWidth / 2, rectHeight / 2, rectWidth / 2, rectHeight / 2, 0, 0, Math.PI * 2);
    } else if (mrx > 0 || mry > 0) {
      ctx.moveTo(mrx, 0);
      ctx.lineTo(rectWidth - mrx, 0);
      ctx.quadraticCurveTo(rectWidth, 0, rectWidth, mry);
      ctx.lineTo(rectWidth, rectHeight - mry);
      ctx.quadraticCurveTo(rectWidth, rectHeight, rectWidth - mrx, rectHeight);
      ctx.lineTo(mrx, rectHeight);
      ctx.quadraticCurveTo(0, rectHeight, 0, rectHeight - mry);
      ctx.lineTo(0, mry);
      ctx.quadraticCurveTo(0, 0, mrx, 0);
      ctx.closePath();
    } else {
      ctx.rect(0, 0, rectWidth, rectHeight);
    }
    ctx.clip();

    // ── Layout: direct scale computation (no iterative loop) ─────────────────
    //
    // Full-size metrics at scaleFactor=1.0, used to compute scale factors.
    const fullMetrics = segments.map((seg) => {
      const fontPx = (seg.fontSize / 100) * ctx.canvas.width;
      ctx.font = `${fontPx}px ${seg.fontFamily}`;
      const m = ctx.measureText(seg.text);
      return {
        width: m.width,
        asc: m.actualBoundingBoxAscent ?? fontPx * 0.8,
        desc: m.actualBoundingBoxDescent ?? fontPx * 0.2,
      };
    });

    // Minimum scaleFactor derived from minfontsize setting.
    // Use the largest segment fontSize as the reference so the dominant (text)
    // segment never shrinks below minfontsize.
    const minfontsize = parsedPositionParts?.minfontsize || 0;
    const refFontSize = Math.max(...segments.map(s => s.fontSize));
    const minScaleFactor = (minfontsize && refFontSize) ? minfontsize / refFontSize : 0.005;

    let scaleFactor;
    let segWidthCaps;

    if (alwaysNewLine) {
      // Each segment is on its own line with the full rectWidth available.
      //
      // Height scale is computed from FULL-SIZE line heights (ignoring width caps).
      // This is the key fix for "jumping": text width caps shrink the text line,
      // which would otherwise free up height and cause scaleFactor to oscillate
      // non-monotonically as the user types — making the icon size jump around.
      // By basing scaleFactor only on full-size heights, it stays stable.
      const fullTotalH =
        fullMetrics.reduce((sum, m) => sum + m.asc + m.desc, 0) +
        lineSpacing * (segments.length - 1);
      scaleFactor = fullTotalH > rectHeight
        ? Math.max(rectHeight / fullTotalH, minScaleFactor)
        : 1.0;

      // Per-segment width caps: each segment independently fits its line width.
      segWidthCaps = fullMetrics.map((m) =>
        m.width > rectWidth ? Math.max(rectWidth / m.width, minScaleFactor) : 1.0
      );
    } else {
      // All segments on one line — uniform scale for combined width and height.
      const combinedWidth = fullMetrics.reduce((sum, m) => sum + m.width, 0);
      const maxH = fullMetrics.reduce((max, m) => Math.max(max, m.asc + m.desc), 0);
      const wScale = combinedWidth > rectWidth ? Math.max(rectWidth / combinedWidth, minScaleFactor) : 1.0;
      const hScale = maxH > rectHeight ? Math.max(rectHeight / maxH, minScaleFactor) : 1.0;
      scaleFactor = Math.min(wScale, hScale);
      segWidthCaps = segments.map(() => 1.0); // no per-segment cap for same-line
    }

    // Clamp scaleFactor to minimum.
    scaleFactor = Math.max(scaleFactor, minScaleFactor);

    // Measure a segment at its effective scale = min(scaleFactor, widthCap).
    const measureSegment = (seg, i) => {
      const cap = segWidthCaps[i] ?? 1.0;
      const fontPx = (seg.fontSize / 100) * ctx.canvas.width * Math.min(scaleFactor, cap);
      ctx.font = `${fontPx}px ${seg.fontFamily}`;
      const m = ctx.measureText(seg.text);
      return {
        seg,
        width: m.width,
        ascent: m.actualBoundingBoxAscent ?? fontPx * 0.8,
        descent: m.actualBoundingBoxDescent ?? fontPx * 0.2,
        fontPx,
      };
    };

    // Build lines.
    const lines = alwaysNewLine
      ? segments.map((s, i) => [measureSegment(s, i)])
      : [segments.map((s, i) => measureSegment(s, i))];

    // Block height for vertical centering.
    const blockHeight =
      lines.reduce((sum, line) => {
        return sum + line.reduce((mx, m) => Math.max(mx, m.ascent + m.descent), 0);
      }, 0) + lineSpacing * (lines.length - 1);

    // Signal overflow only when at the minimum scale AND content still doesn't fit.
    // Check both height-based scaleFactor and per-segment width caps.
    const atMinScale = scaleFactor <= minScaleFactor + 0.001
      || segWidthCaps.some(c => c <= minScaleFactor + 0.001);
    const contentOverflows = scaleFactor < 1.0 || segWidthCaps.some(c => c < 1.0);
    if (atMinScale && contentOverflows) {
      ctx.canvas.dataset.overflow = '1';
    }

    // draw each line, centering vertically and horizontally
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    let y = (rectHeight - blockHeight) / 2;

    lines.forEach((line) => {
      // measure this line's maxAscent+descent
      const lineHeight = line.reduce(
        (mx, m) => Math.max(mx, m.ascent + m.descent),
        0
      );
      const baselineY = y + line.reduce((as, m) => Math.max(as, m.ascent), 0);

      // horizontal centering
      const lineWidth = line.reduce((sum, m) => sum + m.width, 0);
      let x = (rectWidth - lineWidth) / 2;

      line.forEach((m) => {
        ctx.font = `${m.fontPx}px ${m.seg.fontFamily}`;
        ctx.fillStyle = "rgba(0,0,0,0.3)";
        ctx.fillText(m.seg.text, x, baselineY);
        x += m.width;
      });

      // move down past this line plus the spacing
      y += lineHeight + lineSpacing;
    });

    ctx.restore();
    // Return the tightest effective scale so callers can update font size inputs.
    // When text only overflows in width (scaleFactor=1.0 but widthCap<1.0), the
    // minimum cap reflects the true rendered size.
    return Math.min(scaleFactor, ...segWidthCaps);
  }

  appendImage(
    ctx,
    previewPosition,
    rectX,
    rectY,
    rectWidth,
    rectHeight,
    rotateAngle,
    fileInput = false,
    parsedPositionParts = null
  ) {
    // Get the uploaded image file from the specified input element.
    if (!fileInput) {
      const fileInput = this.querySelector(
        "monogram-position-details.active .monogram-block--image .image-upload-label input"
      );
    }

    if (!fileInput.files || fileInput.files.length === 0) {
      console.warn("No image file selected");
      return;
    }

    const file = fileInput.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      const dataURL = reader.result;
      const img = new Image();
      img.src = dataURL;

      img.onload = () => {
        // Determine the scale to fit the image within the target rectangle.
        const iw = img.width;
        const ih = img.height;
        const scaleX = rectWidth / iw;
        const scaleY = rectHeight / ih;
        const scale = Math.min(scaleX, scaleY);

        const destWidth = iw * scale;
        const destHeight = ih * scale;
        const destX = (rectWidth - destWidth) / 2;
        const destY = (rectHeight - destHeight) / 2;

        const rawBorderPct = parsedPositionParts?.borderradius ? parseFloat(parsedPositionParts.borderradius) : 0;
        let irx = Math.min((rawBorderPct / 100) * rectWidth, rectWidth / 2);
        let iry = Math.min((rawBorderPct / 100) * rectHeight, rectHeight / 2);

        ctx.save();
        ctx.translate(rectX, rectY);
        ctx.rotate(rotateAngle);

        ctx.beginPath();
        if (irx >= rectWidth / 2 && iry >= rectHeight / 2) {
          ctx.ellipse(rectWidth / 2, rectHeight / 2, rectWidth / 2, rectHeight / 2, 0, 0, Math.PI * 2);
        } else if (irx > 0 || iry > 0) {
          ctx.moveTo(irx, 0);
          ctx.lineTo(rectWidth - irx, 0);
          ctx.quadraticCurveTo(rectWidth, 0, rectWidth, iry);
          ctx.lineTo(rectWidth, rectHeight - iry);
          ctx.quadraticCurveTo(rectWidth, rectHeight, rectWidth - irx, rectHeight);
          ctx.lineTo(irx, rectHeight);
          ctx.quadraticCurveTo(0, rectHeight, 0, rectHeight - iry);
          ctx.lineTo(0, iry);
          ctx.quadraticCurveTo(0, 0, irx, 0);
          ctx.closePath();
        } else {
          ctx.rect(0, 0, rectWidth, rectHeight);
        }
        ctx.clip();

        // Enable high-quality image smoothing.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        // Draw the image scaled to fit.
        ctx.drawImage(img, destX, destY, destWidth, destHeight);
        ctx.restore();
      };
    };

    reader.readAsDataURL(file);
  }

  // ── Configurator state persistence across combined-listing swaps ─────────
  // Serialises every position's type-select value and all named form inputs
  // (text, number, textarea, checked radio) into data-saved-state on this
  // element.  Because attributes are captured in outerHTML, the cache-save
  // code in global.js automatically includes the state when it stores the
  // section HTML, so it survives a variant swap and is restored below.
  // Returns a storage key that is unique even when multiple monogram blocks
  // share the same input name (e.g. every text block has "properties[_X - Text]").
  // Format: "monogram-block--<type>:<inputName>"  (or just inputName if the
  // input isn't inside a monogram-block, which shouldn't normally happen).
  _inputKey(el) {
    const block = el.closest('.monogram-block');
    const blockClass = block
      ? [...block.classList].find(c => c.startsWith('monogram-block--'))
      : null;
    return blockClass ? `${blockClass}:${el.name}` : el.name;
  }

  saveConfiguratorState() {
    const state = {};
    this.querySelectorAll('monogram-position-details').forEach(pos => {
      const name = pos.dataset.name;
      if (!name) return;
      const posState = { type: '', inputs: {} };

      const typeSelect = pos.querySelector('.monogram-option-select');
      if (typeSelect) posState.type = typeSelect.value;

      pos.querySelectorAll('input[type="text"], input[type="number"], textarea').forEach(input => {
        if (input.name) posState.inputs[this._inputKey(input)] = input.value;
      });

      // Checked radio: one entry per radio name (position-level, not per block).
      // Radio groups are enforced by the browser across the whole form — only one
      // radio with a given name can be checked at a time, so namespacing by block
      // is incorrect and would break restore (setting one block's radio checked
      // silently unchecks the other block's radio with the same name).
      //
      // Font radio groups (.fonts-holder) start with NO radio checked in the HTML.
      // The canvas has its own fallback that uses the first font when nothing is
      // selected, so the preview renders correctly — but without saving the
      // effective font the state would be empty and font selection would not
      // survive a combined-listing swap.  Mirror the canvas fallback: when no font
      // radio is checked, save the first radio in the group as the effective value.
      const seen = new Set();
      pos.querySelectorAll('input[type="radio"]').forEach(radio => {
        if (!radio.name || seen.has(radio.name)) return;
        seen.add(radio.name);
        const sel = `input[type="radio"][name="${CSS.escape(radio.name)}"]`;
        const checked = pos.querySelector(sel + ':checked');
        const isFontGroup = !!radio.closest('.fonts-holder');
        const effective = checked ?? (isFontGroup ? pos.querySelector(sel) : null);
        if (effective) posState.inputs[radio.name] = effective.value;
      });

      // Save drawing stroke data (SignaturePad point arrays).
      // drawing-pad is created lazily by initializeDrawingPad so it only exists
      // after the user has selected the drawing type at least once.
      const drawingPad = pos.querySelector('drawing-pad');
      if (drawingPad?.signaturePad) {
        const strokes = drawingPad.signaturePad.toData();
        if (strokes?.length) posState.drawing = strokes;
      }

      state[name] = posState;
    });
    this.dataset.savedState = JSON.stringify(state);
  }

  // Reads data-saved-state (set by saveConfiguratorState above) and silently
  // restores all input values without dispatching any events.  Block visibility
  // is restored by calling pos.updateBlocks() directly.  A subsequent
  // monogramChanged event (dispatched by product-form.js after reinit) will
  // trigger the canvas re-render with the restored values.
  restoreConfiguratorState() {
    if (!this.dataset.savedState) return;
    let state;
    try { state = JSON.parse(this.dataset.savedState); } catch (e) { return; }

    this.querySelectorAll('monogram-position-details').forEach(pos => {
      const posState = state[pos.dataset.name];
      if (!posState) return;

      // Restore type select and show the correct block without firing events.
      const typeSelect = pos.querySelector('.monogram-option-select');
      if (typeSelect && posState.type) {
        typeSelect.value = posState.type;
        if (typeof pos.updateBlocks === 'function') pos.updateBlocks();
        // Sync clear-button visibility.
        const val = posState.type.replace(/_/g, '-');
        const block = pos.querySelector('.monogram-block--' + val);
        const clearBtn = pos.querySelector('.button--clear-position');
        const optType = block?.dataset.optionType || '';
        if (clearBtn) {
          clearBtn.style.display = (optType && !optType.includes('drawing') && !optType.includes('image')) ? '' : 'none';
        }
      }

      // Restore text / number / textarea values.
      pos.querySelectorAll('input[type="text"], input[type="number"], textarea').forEach(input => {
        if (!input.name) return;
        const key = this._inputKey(input);
        if (Object.prototype.hasOwnProperty.call(posState.inputs, key)) {
          input.value = posState.inputs[key];
        }
      });

      // Restore radio selections (position-level key — no block prefix).
      pos.querySelectorAll('input[type="radio"]').forEach(radio => {
        if (!radio.name) return;
        if (Object.prototype.hasOwnProperty.call(posState.inputs, radio.name)) {
          radio.checked = radio.value === posState.inputs[radio.name];
        } else if (radio.closest('.fonts-holder') && !pos.querySelector(`input[type="radio"][name="${CSS.escape(radio.name)}"]:checked`)) {
          // No saved font and none currently checked: check the first radio in the
          // group so the canvas fallback and the visible selection agree.
          const first = pos.querySelector(`input[type="radio"][name="${CSS.escape(radio.name)}"]`);
          if (first === radio) radio.checked = true;
        }
      });

      // Restore drawing strokes.  drawing-pad is created by initializeDrawingPad
      // which is called from updateBlocks() above, so it exists here if the
      // restored type is drawing.  scaleCanvas() already save/restores strokes on
      // any subsequent resize so strokes survive the modal opening animation.
      if (posState.drawing?.length) {
        const drawingPad = pos.querySelector('drawing-pad');
        if (drawingPad?.signaturePad) {
          drawingPad.signaturePad.fromData(posState.drawing);
          drawingPad.classList.add('completed');
        }
      }
    });

    // Ensure at least one position is marked active so updateAllCanvas can
    // proceed when the next monogramChanged event fires.
    if (!this.querySelector('monogram-position-details.active')) {
      const first = this.querySelector('monogram-position-details');
      if (first) first.classList.add('active');
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  checkForFinalPreview() {
    var showSubmit = 1;
    this.querySelectorAll(
      'monogram-position-details[data-required="true"]'
    ).forEach((monogram) => {
      const selectElement = monogram.querySelector(".select__select");

      const selectedBlock = monogram.querySelector(
        ".monogram-block--" + selectElement.value
      );

      if (selectElement) {
        //console.log(selectElement.value);

        if (selectElement.value == "text") {
          const blockValue = selectedBlock.querySelector(".field__input").value;

          if (blockValue.trim() == "") {
            //console.log("Text block is not valid");
            showSubmit = 0; // block not valid
          }
        } else if (selectElement.value == "icon") {
          if (
            selectedBlock.querySelector(".product-form--radio input:checked") ==
            null
          ) {
            //console.log("Icon block is not valid");
            showSubmit = 0; // block not valid
          }
        } else if (selectElement.value == "icon_text") {
          const blockValue = selectedBlock.querySelector(".field__input").value;
          if (
            blockValue.trim() == "" ||
            selectedBlock.querySelector(".product-form--radio input:checked") ==
              null
          ) {
            //console.log("Text + Icon block is not valid");
            showSubmit = 0; // block not valid
          }
        } else if (selectElement.value == "two_icons") {
          if (
            selectedBlock.querySelector(
              ".font-first-icons-holder .product-form--radio input:checked"
            ) == null ||
            selectedBlock.querySelector(
              ".font-second-icons-holder .product-form--radio input:checked"
            ) == null
          ) {
            //console.log("Two icons block is not valid");
            showSubmit = 0; // block not valid
          }
        } else if (selectElement.value == "drawing") {
          if (selectedBlock.querySelector("drawing-pad.completed") == null) {
            //console.log("Drawing block is not valid");
            showSubmit = 0; // block not valid
          }
        } else if (selectElement.value == "image") {
          if (
            selectedBlock.querySelector('input[type="file"]').files.length == 0
          ) {
            //console.log("Image upload is not valid");
            showSubmit = 0; // block not valid
          }
        } else {
          showSubmit = 0;
        }
      } else {
        showSubmit = 0;
      }
    });
if(this.querySelector(".previewButton")){
    if (showSubmit == 1) {
      this.querySelector(".previewButton").style.display = "block";
    } else {
      this.querySelector(".previewButton").style.display = "none";
    }
}
  }

  previewButtonClicked(event) {
    //console.log("previewButtonClicked");
    const canvasPreview = this.querySelector(".preview-canvas");
    const canvasHolder = this.querySelector(".canvas-holder");
    const finalPreview = this.querySelector(".design-preview-text");
    const canvasPreviewForm = this.querySelector(".monogram-selects-holder");
    const finalPreviewForm = this.querySelector(".design-preview-text");
    const backButton = this.querySelectorAll('.button--back');

    canvasPreviewForm.style.display = "none"
      finalPreviewForm.style.display = "flex";

    backButton.forEach((button) => {
      button.style.display = "block";
    });
    
  }

  updateAllCanvas(event){
    const activeElement = this.querySelector("monogram-position-details.active");
    if (!activeElement) {
      console.warn('[DEBUG] updateAllCanvas called but no ".active" monogram-position-details found.');
      return;
    }

    const activeElementTitle = activeElement.dataset.name;
    const currentColor = this.dataset.currentColor;
    
    // Find the specific slider button that matches both the active position and the current product color
    const selector = `.slider-counter__link[data-position-name='${activeElementTitle}']`;
    //console.log(`[DEBUG] updateAllCanvas trying to find and click slider button with selector: ${selector}`);

    const active_slider_button = this.querySelector(selector);
    if (active_slider_button) {
      // Check if the button is already active to prevent redundant clicks from slider's own events
      if (!active_slider_button.classList.contains('slider-counter__link--active')) {
        active_slider_button.click();
      }
    } else {
      console.warn(`[DEBUG] Did not find a slider button for position '${activeElementTitle}'.`);
    }

    // After a potential slide change, redraw all canvases to ensure they are up to date with the latest input values.
    const allVisibleCanvases = this.querySelectorAll(
      "configurator-preview .final-preview-position"
    );

    allVisibleCanvases.forEach((canvas) => {
      this.updateMonogram(canvas);
    });
  }

  returnButtonClicked(event) {
    const canvasPreview = this.querySelector(".preview-canvas");
    const canvasHolder = this.querySelector(".canvas-holder");
    const finalPreview = this.querySelector(".design-preview-images");
    const canvasPreviewForm = this.querySelector(".monogram-selects-holder");
    const finalPreviewForm = this.querySelector(".design-preview-text");
    const backButton = this.querySelectorAll('.button--back');

    if (canvasPreviewForm) {
    canvasPreviewForm.style.display = "block";
    }
    if (finalPreviewForm) {
    finalPreviewForm.style.display = "none";
    }
     backButton.forEach((button) => {
      button.style.display = "none";
    });
  }

  toggleFinalPreviews() {
    this.finalPreviews = this.querySelectorAll(".configurator__media-list li");
    var pickedColourInput = this.querySelector(
      '.product-options-select .product-form--radios[data-option="colour"] input[type="radio"]:checked'
    );

    if (!pickedColourInput) {
      var pickedColourInput = this.querySelector(
        '.product-options-select .product-form--radios[data-option="colour"] input[type="radio"]'
      );
    }
if(pickedColourInput){
    const pickedColourName = pickedColourInput.dataset.value;
    this.finalPreviews.forEach((preview) => {
      if (preview.dataset.colour == pickedColourName) {
        preview.style.display = "block";
        preview.classList.add("active");
      } else {
        preview.style.display = "none";
        preview.classList.remove("active");
      }
    });
}
  }
}

customElements.define("configurator-modal", ConfiguratorModal);

